import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ArrowLeft, LayoutDashboard, Loader2, Save, Plus, Trash2,
  Move, Filter, GitBranch, Search, ChevronUp, ChevronDown, Settings, X,
  Maximize2, Minimize2, Eye, EyeOff, Pencil,
} from 'lucide-react'
import type { EmbeddedDesignerProps } from './embeddedDesigner'
import {
  loadDashboardObject, saveDashboardObject,
  listProjectVisuals, loadVisualObject, type ProjectVisualSummary,
  listProjectReports, loadReportObject, type ProjectReportSummary,
  listProjectDashboards, type ProjectDashboardSummary,
} from '@/api/designers'
import { useProjectStore } from '@/store/projectStore'
import { useBuilderContextStore } from '@/store/builderContextStore'
import { useUnsavedChangesGuard } from '@/hooks/useUnsavedChangesGuard'
import { useAgentDeletion, useAgentRefresh } from '@/hooks/useAgentRefresh'
import { useDesignHistory } from '@/hooks/useDesignHistory'
import UndoRedoButtons from '@/components/common/UndoRedoButtons'
import DiscardChangesModal from '@/components/common/DiscardChangesModal'
import type {
  DashboardModel, DashboardBody, DashboardVisual,
  DashboardGlobalCriterion, DashboardDrillThrough,
  DashboardDrillTargetType,
  DashboardOperator, DashboardDataType,
  DashboardCriterionBinding, DashboardBindType,
} from './dashboard/types'
import {
  blankDashboardModel, sanitizeDashboardModel,
  blankVisual, blankCriterion, blankDrillThrough,
  syncBroadcastFromBindings,
} from './dashboard/helpers'
import { extractVisualParams, type VisualParam } from './dashboard/visualParams'
import CriteriaLookupItemChange from './visual/CriteriaLookupItemChange'
import CriteriaDefaultField from './visual/CriteriaDefaultField'
import CriteriaValidationsEditor from './visual/CriteriaValidationsEditor'
import DashboardPreviewModal from './dashboard/DashboardPreviewModal'
import PreviewThumbnail, { bustPreview } from '@/pages/workbench/analytics/PreviewThumbnail'
import VisualDesignerPage from './VisualDesignerPage'
import { listProjectVariables, type ProjectVariable } from '@/api/projectVariables'
import ObjectPickerField from './page/ObjectPickerField'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import { useRunOnlyRedirect } from '@/auth/useRunOnlyRedirect'
import {
  DASH_COLS, DASH_STEP, dashSnap, dashGridContainerStyle, dashTileStyle,
  DASHBOARD_THEME_COLORS, DASHBOARD_THEME_DEFAULT, normalizeDashboardThemeColor,
} from '@twasta/visual-engine'

const OPERATOR_OPTIONS: DashboardOperator[] = [
  '=', '!=', '>', '>=', '<', '<=', 'between', 'in', 'like',
]
const DATA_TYPE_OPTIONS: DashboardDataType[] = ['string', 'number', 'date']

type Tab = 'layout' | 'criteria' | 'drill'

// VISUAL_CONFIG_REF stores the on-disk filename (`model_<obj>.json`).
// The visual loader takes obj_name, so strip the wrapper.
function visualRefToObjName(ref: string): string {
  if (!ref) return ''
  const stem = ref.endsWith('.json') ? ref.slice(0, -5) : ref
  return stem.startsWith('model_') ? stem.slice(6) : stem
}

// The visualContents cache key for a tile's underlying model: the visual's
// VISUAL_CONFIG_REF filename, or `report:<obj>` for a report tile (report obj
// names can't collide with `model_*.json` filenames, so one cache serves both).
function tileContentKey(tile: DashboardVisual): string {
  if (tile.TILE_KIND === 'report') {
    return tile.REPORT_OBJ ? `report:${tile.REPORT_OBJ}` : ''
  }
  return tile.VISUAL_CONFIG_REF || ''
}

// Resolve overlaps after tiles are moved/resized. The anchors (the tiles the
// user just touched — one, or a whole multi-selection) are treated as
// immovable; any other tile that overlaps a "fixed" tile and starts at or
// below it horizontally + vertically gets pushed down to the fixed tile's
// bottom. Cascades — a pushed tile can in turn push tiles below it. Tiles
// whose top sits strictly above the anchor's top are left alone (the user's
// grow goes downward, not upward).
function resolveDownwardCollisions(
  visuals: DashboardVisual[],
  anchorIds: string | readonly string[],
): void {
  const anchors = new Set(
    typeof anchorIds === 'string' ? [anchorIds] : anchorIds
  )
  const fixed = new Set<string>(anchors)
  // Bounded loop — every iteration either moves a tile or exits, and there
  // are finitely many tiles, so this always terminates.
  for (let pass = 0; pass < 200; pass++) {
    let changed = false
    for (const a of visuals) {
      if (!fixed.has(a.VISUAL_ID)) continue
      const aLeft = a.POSITION.X
      const aRight = a.POSITION.X + a.POSITION.WIDTH
      const aTop = a.POSITION.Y
      const aBottom = a.POSITION.Y + a.POSITION.HEIGHT
      for (const b of visuals) {
        if (b.VISUAL_ID === a.VISUAL_ID) continue
        if (anchors.has(b.VISUAL_ID)) continue
        const bLeft = b.POSITION.X
        const bRight = b.POSITION.X + b.POSITION.WIDTH
        if (bRight <= aLeft || bLeft >= aRight) continue  // no horizontal overlap
        const bTop = b.POSITION.Y
        const bBottom = b.POSITION.Y + b.POSITION.HEIGHT
        if (bBottom <= aTop || bTop >= aBottom) continue  // no vertical overlap
        if (bTop < aTop) continue  // tile sits above the fixed one — leave it
        b.POSITION.Y = aBottom
        fixed.add(b.VISUAL_ID)
        changed = true
      }
    }
    if (!changed) break
  }
}

export default function DashboardDesignerPage({
  embedded = false,
  hostFullscreen = false,
  embedProject,
  embedObjName,
  onDirtyChange,
  registerSave,
  onSaved,
}: EmbeddedDesignerProps = {}) {
  const params = useParams<{ project: string; objName: string }>()
  const project = embedProject ?? params.project
  useRunOnlyRedirect(project)
  const objName = embedObjName ?? params.objName
  const navigate = useNavigate()
  const location = useLocation()
  const projectName = project || ''
  const objectName = objName || ''
  const { currentProject, selectProject } = useProjectStore()

  useEffect(() => {
    if (project && currentProject?.application.folder_name !== project) {
      selectProject(project)
    }
  }, [project, currentProject, selectProject])

  const [model, setModel] = useState<DashboardModel>(() =>
    blankDashboardModel(objectName)
  )
  const [projectVisuals, setProjectVisuals] = useState<ProjectVisualSummary[]>([])
  // Project dashboards — drives the Drill-through target-dashboard picker.
  const [projectDashboards, setProjectDashboards] = useState<ProjectDashboardSummary[]>([])
  // Project reports (obj_type R) — drives the report-tile picker.
  const [projectReports, setProjectReports] = useState<ProjectReportSummary[]>([])
  // Cache of loaded visual JSONs keyed by VISUAL_CONFIG_REF (filename) —
  // report tiles share the cache under a `report:<obj>` key (see
  // tileContentKey). Used to extract each tile's prompt variables for the
  // bindings UI. Refs that fail to load are stored as `null` so we don't
  // refetch.
  const [visualContents, setVisualContents] = useState<
    Record<string, unknown | null>
  >({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { canEditObjectsOrLoading: canEdit } = useProjectPermissions()
  const [propsOpen, setPropsOpen] = useState(false)
  // Full-screen toggle for the embedded ("Design" tab) usage only.
  const [embedFullscreen, setEmbedFullscreen] = useState(false)
  // Live preview of the current (possibly unsaved) tile arrangement — rendered
  // in the shared standalone DashboardPreviewModal (outside this designer's
  // tree) so the card "View" and app navigation reuse the same surface.
  const [previewOpen, setPreviewOpen] = useState(false)
  // The visual obj name being edited in the overlay (via a tile's "Edit Visual"
  // button) — opens the full Visual designer without leaving the dashboard.
  const [editVisualObj, setEditVisualObj] = useState<string | null>(null)
  const [tab, setTab] = useState<Tab>('layout')
  const [selectedVisualId, setSelectedVisualId] = useState<string | null>(null)
  // Multi-selection on the layout grid (Ctrl/Cmd/Shift-click). Always a
  // superset of {selectedVisualId}; the primary drives the inspector.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(new Set())
  const [selectedCriterionIdx, setSelectedCriterionIdx] = useState<number | null>(
    null
  )
  const [selectedDrillIdx, setSelectedDrillIdx] = useState<number | null>(null)

  // Project variables — so a global criterion's default can bind a :<var> token
  // (same token-aware default editor the visual designer uses).
  const [projectVars, setProjectVars] = useState<ProjectVariable[]>([])
  useEffect(() => {
    if (!projectName) { setProjectVars([]); return }
    let cancelled = false
    listProjectVariables(projectName)
      .then((vars) => { if (!cancelled) setProjectVars(vars) })
      .catch(() => { if (!cancelled) setProjectVars([]) })
    return () => { cancelled = true }
  }, [projectName])

  // Snapshot of last-saved model used for dirty detection.
  const [savedSnapshot, setSavedSnapshot] = useState<string | null>(null)
  const isDirty = savedSnapshot !== null && JSON.stringify(model) !== savedSnapshot
  const { guard, showDiscardModal, confirmDiscard, cancelDiscard } =
    useUnsavedChangesGuard(isDirty)
  const history = useDesignHistory(model, setModel, { disabled: !canEdit })
  const { reset: resetHistory } = history

  // Reload the model when an AI agent run touches project files so the open
  // designer reflects the agent's edits without a hard browser refresh.
  // Deferred while dirty so in-progress edits are never clobbered.
  const agentRefresh = useAgentRefresh(projectName, isDirty)

  // An agent run that DELETED this object must close it, not refetch it — a
  // refetch 404s and leaves the removed object on screen (and a later Save
  // would resurrect it). Deletions only became visible to the client in Phase
  // 0.8 of docs/ssh_agent_console_support_plan.md.
  useAgentDeletion(projectName, objectName, 'dashboard_models', () => {
    if (embedded) return   // the host tab owns navigation
    navigate('/workbench/analytics')
  })

  // Publish the open dashboard as the Builder/Ask-AI "open object" so the agent
  // resolves "this dashboard" / "this" / "here" to it instead of guessing —
  // parity with the nav tree and workflow designer.
  useEffect(() => {
    if (!objectName) return
    useBuilderContextStore.getState().setOpenObject({
      obj_name: objectName,
      obj_type: 'D',
      title: objectName,
    })
    return () => useBuilderContextStore.getState().setOpenObject(null)
  }, [objectName])

  // Load dashboard + project visuals (so the tile editor can pick refs).
  useEffect(() => {
    let cancelled = false
    if (!projectName || !objectName) return
    setLoading(true)
    Promise.all([
      loadDashboardObject(projectName, objectName).catch((err) => {
        console.error('Failed to load dashboard:', err)
        toast.error('Failed to load dashboard')
        return null
      }),
      listProjectVisuals(projectName).catch(() => null),
      listProjectDashboards(projectName).catch(() => null),
      listProjectReports(projectName).catch(() => null),
    ])
      .then(([data, visuals, dashboards, reports]) => {
        if (cancelled) return
        const loaded = data && data.content
          ? sanitizeDashboardModel(data.content, objectName)
          : blankDashboardModel(objectName)
        setModel(loaded)
        setSavedSnapshot(JSON.stringify(loaded))
        // A (re)load replaces the model wholesale — stale undo entries would
        // restore a different/older dashboard.
        resetHistory()
        if (visuals && visuals.success) {
          setProjectVisuals(visuals.visuals || [])
        }
        if (dashboards && dashboards.success) {
          setProjectDashboards(dashboards.dashboards || [])
        }
        if (reports && reports.success) {
          setProjectReports(reports.reports || [])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [projectName, objectName, agentRefresh, resetHistory])

  // Load each tile's referenced visual JSON (or report model, for report
  // tiles) so we can show its prompt variables in the bindings UI. Each ref
  // is fetched at most once (cache by tileContentKey); missing refs are
  // stored as null sentinels.
  useEffect(() => {
    if (!projectName) return
    const refs = Array.from(
      new Set(model.DASHBOARD.VISUALS.map(tileContentKey).filter(Boolean))
    )
    const missing = refs.filter((r) => !(r in visualContents))
    if (missing.length === 0) return
    let cancelled = false
    Promise.all(
      missing.map(async (ref) => {
        try {
          if (ref.startsWith('report:')) {
            const res = await loadReportObject(projectName, ref.slice('report:'.length))
            return [ref, res?.content ?? null] as const
          }
          const objName = visualRefToObjName(ref)
          if (!objName) return [ref, null] as const
          const res = await loadVisualObject(projectName, objName)
          return [ref, res?.content ?? null] as const
        } catch (err) {
          console.error('Failed to load tile content', ref, err)
          return [ref, null] as const
        }
      })
    ).then((pairs) => {
      if (cancelled) return
      setVisualContents((prev) => {
        const next = { ...prev }
        for (const [ref, content] of pairs) {
          next[ref] = content
        }
        return next
      })
    })
    return () => {
      cancelled = true
    }
  }, [projectName, model.DASHBOARD.VISUALS, visualContents])

  // ── Mutators ────────────────────────────────────────────────
  const updateBody = useCallback(
    (mutator: (b: DashboardBody) => void) => {
      setModel((prev) => {
        const next = JSON.parse(JSON.stringify(prev)) as DashboardModel
        mutator(next.DASHBOARD)
        return next
      })
    },
    []
  )

  const handleHeaderChange = useCallback(
    <K extends keyof DashboardBody>(field: K, value: DashboardBody[K]) => {
      updateBody((b) => {
        ;(b as DashboardBody)[field] = value
      })
    },
    [updateBody]
  )

  const handleAddVisual = useCallback(() => {
    updateBody((b) => {
      const tile = blankVisual(b.VISUALS)
      // Lay out new tiles below the lowest existing tile.
      const maxBottom = b.VISUALS.reduce(
        (acc, v) => Math.max(acc, v.POSITION.Y + v.POSITION.HEIGHT),
        0
      )
      tile.POSITION.Y = maxBottom
      b.VISUALS.push(tile)
      setSelectedVisualId(tile.VISUAL_ID)
      setSelectedIds(new Set([tile.VISUAL_ID]))
    })
  }, [updateBody])

  const handleDeleteVisual = useCallback(
    (visualId: string) => {
      updateBody((b) => {
        b.VISUALS = b.VISUALS.filter((v) => v.VISUAL_ID !== visualId)
        // Drop broadcast targets that referred to it.
        for (const c of b.GLOBAL_CRITERIA) {
          c.BROADCAST_TO = c.BROADCAST_TO.filter((id) => id !== visualId)
        }
        // Drop drill-through entries originating from it.
        b.DRILL_THROUGH = b.DRILL_THROUGH.filter(
          (d) => d.SOURCE_VISUAL_ID !== visualId
        )
      })
      setSelectedVisualId(null)
      setSelectedIds(new Set())
    },
    [updateBody]
  )

  const handleVisualFieldChange = useCallback(
    <K extends keyof DashboardVisual>(
      visualId: string,
      field: K,
      value: DashboardVisual[K]
    ) => {
      updateBody((b) => {
        const v = b.VISUALS.find((x) => x.VISUAL_ID === visualId)
        if (!v) return
        if (field === 'VISUAL_CONFIG_REF' && typeof value === 'string'
            && value !== v.VISUAL_CONFIG_REF) {
          // The pointed-to visual changed — its variables may differ, so
          // drop stale bindings rather than silently hiding them.
          v.CRITERIA_BINDINGS = []
          const { GLOBAL_CRITERIA: nextGc } = syncBroadcastFromBindings(b)
          b.GLOBAL_CRITERIA = nextGc
        }
        if ((field === 'REPORT_OBJ' && typeof value === 'string' && value !== v.REPORT_OBJ)
            || (field === 'TILE_KIND' && value !== (v.TILE_KIND ?? 'visual'))) {
          // The tile now renders a different report — or switched between
          // visual and report content entirely — so its bound variables no
          // longer apply. Drop stale bindings rather than silently hiding them.
          v.CRITERIA_BINDINGS = []
          const { GLOBAL_CRITERIA: nextGc } = syncBroadcastFromBindings(b)
          b.GLOBAL_CRITERIA = nextGc
        }
        if (field === 'VISUAL_ID' && typeof value === 'string') {
          // Cascade rename to broadcast targets and drill-through sources.
          const oldId = v.VISUAL_ID
          for (const c of b.GLOBAL_CRITERIA) {
            c.BROADCAST_TO = c.BROADCAST_TO.map((id) => (id === oldId ? value : id))
          }
          for (const d of b.DRILL_THROUGH) {
            if (d.SOURCE_VISUAL_ID === oldId) d.SOURCE_VISUAL_ID = value
          }
          setSelectedVisualId(value)
          setSelectedIds((prev) =>
            prev.has(oldId)
              ? new Set([...prev].map((x) => (x === oldId ? value : x)))
              : prev
          )
        }
        ;(v as Record<string, unknown>)[field as string] = value
      })
    },
    [updateBody]
  )

  const handleVisualPositionChange = useCallback(
    (
      visualId: string,
      field: keyof DashboardVisual['POSITION'],
      value: number
    ) => {
      updateBody((b) => {
        const v = b.VISUALS.find((x) => x.VISUAL_ID === visualId)
        if (!v) return
        v.POSITION = { ...v.POSITION, [field]: dashSnap(value) }
        // Push tiles that now overlap the anchor downward (cascading) so the
        // user's resize/move never produces hidden overlaps.
        resolveDownwardCollisions(b.VISUALS, visualId)
      })
    },
    [updateBody]
  )

  // Grid selection. 'single' replaces the selection, 'toggle' is a
  // Ctrl/Cmd/Shift-click add/remove, 'primary' makes an already-selected
  // tile the inspector target without collapsing a multi-selection.
  const handleSelectVisual = useCallback(
    (id: string | null, mode: 'single' | 'toggle' | 'primary' = 'single') => {
      if (!id) {
        setSelectedVisualId(null)
        setSelectedIds(new Set())
        return
      }
      if (mode === 'toggle') {
        const next = new Set(selectedIds)
        if (next.has(id)) {
          next.delete(id)
          setSelectedVisualId((cur) =>
            cur === id ? [...next].pop() ?? null : cur
          )
        } else {
          next.add(id)
          setSelectedVisualId(id)
        }
        setSelectedIds(next)
        return
      }
      if (mode === 'primary' && selectedIds.has(id)) {
        setSelectedVisualId(id)
        return
      }
      setSelectedVisualId(id)
      setSelectedIds(new Set([id]))
    },
    [selectedIds]
  )

  // Apply absolute X/Y moves to a set of tiles in one update (group drag),
  // then push any newly-overlapped tiles below the whole group.
  const handleMoveTiles = useCallback(
    (moves: Array<{ id: string; X: number; Y: number }>) => {
      if (!moves.length) return
      updateBody((b) => {
        for (const m of moves) {
          const v = b.VISUALS.find((x) => x.VISUAL_ID === m.id)
          if (v) v.POSITION = { ...v.POSITION, X: dashSnap(m.X), Y: dashSnap(m.Y) }
        }
        resolveDownwardCollisions(b.VISUALS, moves.map((m) => m.id))
      })
    },
    [updateBody]
  )

  // Arrow-key nudge: move the whole selection by (dx, dy) layout units,
  // clamped so no selected tile leaves the 12-column canvas.
  const handleNudgeSelection = useCallback(
    (dx: number, dy: number) => {
      const ids = [...selectedIds]
      if (!ids.length) return
      updateBody((b) => {
        const tiles = b.VISUALS.filter((v) => selectedIds.has(v.VISUAL_ID))
        if (!tiles.length) return
        const minX = Math.min(...tiles.map((t) => t.POSITION.X))
        const maxRight = Math.max(
          ...tiles.map((t) => t.POSITION.X + t.POSITION.WIDTH)
        )
        const minY = Math.min(...tiles.map((t) => t.POSITION.Y))
        const cdx = Math.max(-minX, Math.min(DASH_COLS - maxRight, dx))
        const cdy = Math.max(-minY, dy)
        if (!cdx && !cdy) return
        for (const t of tiles) {
          t.POSITION = {
            ...t.POSITION,
            X: dashSnap(t.POSITION.X + cdx),
            Y: dashSnap(t.POSITION.Y + cdy),
          }
        }
        resolveDownwardCollisions(b.VISUALS, ids)
      })
    },
    [updateBody, selectedIds]
  )

  // Keyboard nudging on the layout tab — arrows move the selection a
  // quarter cell, Shift+arrows a whole cell. Skipped while typing in a
  // field so the inspector inputs keep their native arrow behavior.
  useEffect(() => {
    if (tab !== 'layout' || selectedIds.size === 0) return
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null
      if (
        t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA'
          || t.tagName === 'SELECT' || t.isContentEditable)
      ) return
      const step = e.shiftKey ? 1 : DASH_STEP
      let dx = 0
      let dy = 0
      if (e.key === 'ArrowLeft') dx = -step
      else if (e.key === 'ArrowRight') dx = step
      else if (e.key === 'ArrowUp') dy = -step
      else if (e.key === 'ArrowDown') dy = step
      else return
      e.preventDefault()
      handleNudgeSelection(dx, dy)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [tab, selectedIds, handleNudgeSelection])

  // Set a single binding entry on a tile. The bindings array is keyed by
  // VISUAL_PARAM (one row per visual variable); we replace any existing
  // entry for that name. Passing `null` removes the entry entirely.
  const handleSetBinding = useCallback(
    (visualId: string, visualParam: string, binding: DashboardCriterionBinding | null) => {
      updateBody((b) => {
        const v = b.VISUALS.find((x) => x.VISUAL_ID === visualId)
        if (!v) return
        const list = Array.isArray(v.CRITERIA_BINDINGS)
          ? [...v.CRITERIA_BINDINGS]
          : []
        const idx = list.findIndex((x) => x.VISUAL_PARAM === visualParam)
        if (binding === null) {
          if (idx >= 0) list.splice(idx, 1)
        } else if (idx >= 0) {
          list[idx] = binding
        } else {
          list.push(binding)
        }
        v.CRITERIA_BINDINGS = list
        // Keep BROADCAST_TO consistent so the runtime sees the same
        // tile-criterion edges the designer just expressed.
        const { GLOBAL_CRITERIA: nextGc } = syncBroadcastFromBindings(b)
        b.GLOBAL_CRITERIA = nextGc
      })
    },
    [updateBody]
  )

  const handleAddCriterion = useCallback(() => {
    updateBody((b) => {
      b.GLOBAL_CRITERIA.push(blankCriterion())
      setSelectedCriterionIdx(b.GLOBAL_CRITERIA.length - 1)
    })
  }, [updateBody])

  const handleDeleteCriterion = useCallback(
    (idx: number) => {
      updateBody((b) => {
        const removed = b.GLOBAL_CRITERIA[idx]
        b.GLOBAL_CRITERIA.splice(idx, 1)
        // Drop any tile bindings that pointed at the removed param.
        if (removed?.PARAM_NAME) {
          for (const v of b.VISUALS) {
            if (!v.CRITERIA_BINDINGS) continue
            v.CRITERIA_BINDINGS = v.CRITERIA_BINDINGS.filter(
              (bnd) => !(bnd.BIND_TYPE === 'global' && bnd.GLOBAL_PARAM === removed.PARAM_NAME)
            )
          }
        }
      })
      setSelectedCriterionIdx(null)
    },
    [updateBody]
  )

  const handleCriterionChange = useCallback(
    <K extends keyof DashboardGlobalCriterion>(
      idx: number,
      field: K,
      value: DashboardGlobalCriterion[K]
    ) => {
      updateBody((b) => {
        const c = b.GLOBAL_CRITERIA[idx]
        if (!c) return
        // Cascade PARAM_NAME renames into tile bindings so they stay wired.
        if (field === 'PARAM_NAME' && typeof value === 'string') {
          const oldName = c.PARAM_NAME
          if (oldName && oldName !== value) {
            for (const v of b.VISUALS) {
              if (!v.CRITERIA_BINDINGS) continue
              for (const bnd of v.CRITERIA_BINDINGS) {
                if (bnd.BIND_TYPE === 'global' && bnd.GLOBAL_PARAM === oldName) {
                  bnd.GLOBAL_PARAM = value
                }
              }
            }
          }
        }
        ;(c as Record<string, unknown>)[field as string] = value
      })
    },
    [updateBody]
  )

  const handleToggleBroadcast = useCallback(
    (idx: number, visualId: string) => {
      updateBody((b) => {
        const c = b.GLOBAL_CRITERIA[idx]
        if (!c) return
        const i = c.BROADCAST_TO.indexOf(visualId)
        if (i >= 0) {
          c.BROADCAST_TO.splice(i, 1)
        } else {
          c.BROADCAST_TO.push(visualId)
        }
      })
    },
    [updateBody]
  )

  const handleAddDrill = useCallback(() => {
    updateBody((b) => {
      b.DRILL_THROUGH.push(blankDrillThrough())
      setSelectedDrillIdx(b.DRILL_THROUGH.length - 1)
    })
  }, [updateBody])

  const handleDeleteDrill = useCallback(
    (idx: number) => {
      updateBody((b) => {
        b.DRILL_THROUGH.splice(idx, 1)
      })
      setSelectedDrillIdx(null)
    },
    [updateBody]
  )

  const handleDrillFieldChange = useCallback(
    <K extends keyof DashboardDrillThrough>(
      idx: number,
      field: K,
      value: DashboardDrillThrough[K]
    ) => {
      updateBody((b) => {
        const d = b.DRILL_THROUGH[idx]
        if (!d) return
        ;(d as Record<string, unknown>)[field as string] = value
      })
    },
    [updateBody]
  )

  const handleDrillMappingChange = useCallback(
    (idx: number, oldKey: string, newKey: string, newValue: string) => {
      updateBody((b) => {
        const d = b.DRILL_THROUGH[idx]
        if (!d) return
        const next: Record<string, string> = {}
        for (const [k, v] of Object.entries(d.PARAM_MAPPING)) {
          if (k === oldKey) {
            if (newKey) next[newKey] = newValue
          } else {
            next[k] = v
          }
        }
        d.PARAM_MAPPING = next
      })
    },
    [updateBody]
  )

  const handleAddDrillMapping = useCallback(
    (idx: number) => {
      updateBody((b) => {
        const d = b.DRILL_THROUGH[idx]
        if (!d) return
        // Find an unused empty key slot.
        let n = 1
        let key = `SOURCE_COLUMN_${n}`
        while (key in d.PARAM_MAPPING) {
          n += 1
          key = `SOURCE_COLUMN_${n}`
        }
        d.PARAM_MAPPING = { ...d.PARAM_MAPPING, [key]: '' }
      })
    },
    [updateBody]
  )

  const handleDeleteDrillMapping = useCallback(
    (idx: number, key: string) => {
      updateBody((b) => {
        const d = b.DRILL_THROUGH[idx]
        if (!d) return
        const next = { ...d.PARAM_MAPPING }
        delete next[key]
        d.PARAM_MAPPING = next
      })
    },
    [updateBody]
  )

  // ── Save / Generate ────────────────────────────────────────
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!projectName || !objectName) return false
    setSaving(true)
    try {
      await saveDashboardObject(projectName, objectName, model)
      setSavedSnapshot(JSON.stringify(model))
      toast.success('Dashboard saved')
      // Tell the embedding shell the object now exists on disk: its Live
      // Preview tab is gated on that, answered once when the node was
      // selected, so a first save would otherwise leave the tab missing
      // until the node was reselected.
      onSaved?.()
      return true
    } catch (err) {
      console.error('Save failed', err)
      toast.error('Failed to save dashboard')
      return false
    } finally {
      setSaving(false)
    }
  }, [projectName, objectName, model, onSaved])

  // When embedded, report dirty state + register save so the workbench shell
  // can guard navigation away and offer "Save & Continue".
  useEffect(() => {
    if (!embedded || !onDirtyChange) return
    onDirtyChange(isDirty)
    return () => onDirtyChange(false)
  }, [embedded, isDirty, onDirtyChange])
  useEffect(() => {
    if (!embedded || !registerSave) return
    registerSave(handleSave)
  }, [embedded, registerSave, handleSave])

  const handleBack = useCallback(() => {
    const selectedNodeId = (location.state as { selectedNodeId?: string })?.selectedNodeId
    guard(() => navigate('/workbench', { state: { selectedNodeId } }))
  }, [guard, location.state, navigate])

  const handleSaveAndContinue = useCallback(async () => {
    const ok = await handleSave()
    if (ok) confirmDiscard()
  }, [handleSave, confirmDiscard])

  // ── Derived ────────────────────────────────────────────────
  const body = model.DASHBOARD
  const selectedVisual = useMemo(
    () =>
      selectedVisualId
        ? body.VISUALS.find((v) => v.VISUAL_ID === selectedVisualId) || null
        : null,
    [selectedVisualId, body.VISUALS]
  )
  const selectedCriterion =
    selectedCriterionIdx != null ? body.GLOBAL_CRITERIA[selectedCriterionIdx] : null
  const selectedDrill =
    selectedDrillIdx != null ? body.DRILL_THROUGH[selectedDrillIdx] : null

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: '100%' }}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span style={{ marginLeft: 8 }}>Loading dashboard…</span>
      </div>
    )
  }

  return (
    <div style={embedded && embedFullscreen ? {
      position: 'fixed', inset: 0, zIndex: 1000, background: 'var(--color-bg)',
      display: 'flex', flexDirection: 'column', padding: '0 12px',
    } : {
      display: 'flex', flexDirection: 'column', height: '100%',
    }}>
      {/* Toolbar — card surface, mirrors the Visual designer header (§9.2) */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '9px 12px',
          flexShrink: 0,
          gap: 12,
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          margin: '6px 0 0',
        }}
      >
        <div className="flex items-center gap-2" style={{ minWidth: 0, flex: 1 }}>
          {!embedded && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={handleBack}
            >
              <ArrowLeft size={16} />
            </button>
          )}
          {/* Object-type chip — dashboards are green (D) across the
              analytics surfaces (spec §0.4). */}
          <span style={{
            width: 30, height: 30, borderRadius: 8, flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: '#3fb950', color: '#fff',
          }}>
            <LayoutDashboard size={17} />
          </span>
          {/* Inline-editable title + description. The DASHBOARD_NAME is
              the prominent line; DASHBOARD_DESCR is the smaller one
              underneath. Both render as styled text but become text /
              textarea inputs on focus. The obj_name + project show as a
              tiny breadcrumb on top so the user always knows what file
              they're editing — but is read-only (rename via prototype). */}
          <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Breadcrumb — mono 12px faint (spec §9.2). */}
            <div style={{
              fontSize: 14,
              color: 'var(--color-text-faint)',
              fontFamily: MONO_FONT,
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {projectName} / {objectName} · dashboard
            </div>
            <input
              type="text"
              value={body.DASHBOARD_NAME}
              onChange={(e) => handleHeaderChange('DASHBOARD_NAME', e.target.value)}
              placeholder="Dashboard name"
              title="Click to edit dashboard name"
              style={{
                background: 'transparent',
                border: 'none',
                padding: '1px 4px',
                margin: '-1px -4px',
                fontSize: '1.2625rem',
                fontWeight: 700,
                letterSpacing: '-0.3px',
                color: 'var(--color-text)',
                outline: 'none',
                borderRadius: 4,
                width: '100%',
                minWidth: 0,
              }}
              onFocus={(e) => { e.currentTarget.style.background = 'var(--color-surface-2)' }}
              onBlur={(e) => { e.currentTarget.style.background = 'transparent' }}
            />
            <input
              type="text"
              value={body.DASHBOARD_DESCR}
              onChange={(e) => handleHeaderChange('DASHBOARD_DESCR', e.target.value)}
              placeholder="Add a description (click to edit)…"
              title="Click to edit description"
              style={{
                background: 'transparent',
                border: 'none',
                padding: '1px 4px',
                margin: '-1px -4px',
                fontSize: 15,
                color: 'var(--color-text-secondary)',
                outline: 'none',
                borderRadius: 4,
                width: '100%',
                minWidth: 0,
                fontStyle: body.DASHBOARD_DESCR ? 'normal' : 'italic',
              }}
              onFocus={(e) => { e.currentTarget.style.background = 'var(--color-surface-2)' }}
              onBlur={(e) => { e.currentTarget.style.background = 'transparent' }}
            />
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setPropsOpen(true)}
            title="Dashboard properties"
            style={{
              flexShrink: 0, width: 34, height: 34, padding: 0,
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid var(--color-border)', borderRadius: 8,
            }}
          >
            <Settings size={16} />
          </button>
        </div>

        <div className="flex items-center gap-2">
          {canEdit && <UndoRedoButtons history={history} />}
          {/* Preview — the shared standalone modal (a fixed overlay, outside
              this designer's tree) so the same live-preview surface serves the
              card "View" and app navigation without a duplicate implementation. */}
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setPreviewOpen(true)}
            title="Preview this dashboard with live data"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <Eye size={14} /> Preview
          </button>
          {canEdit ? (
            <button
              className={`btn btn-sm ${isDirty ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handleSave}
              disabled={saving}
              title={isDirty ? 'You have unsaved changes' : 'No changes to save'}
              style={isDirty ? { boxShadow: '0 0 0 3px var(--color-accent-weak)' } : undefined}
            >
              {saving ? (
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <Save size={14} />
              )}
              {saving ? 'Saving…' : isDirty ? 'Save *' : 'Save'}
            </button>
          ) : (
            <ReadOnlyBadge />
          )}
          {embedded && !hostFullscreen && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setEmbedFullscreen((f) => !f)}
              title={embedFullscreen ? 'Restore' : 'Full screen'}
            >
              {embedFullscreen ? <Minimize2 size={15} /> : <Maximize2 size={15} />}
            </button>
          )}
        </div>
      </div>

      {/* Header metadata strip removed — dashboard-level fields live in
          the right panel (Layout tab → "Dashboard properties") so the
          grid keeps the maximum vertical real estate. */}

      {/* Tab bar — underline tabs on a transparent row (spec §0.3) */}
      <div
        style={{
          display: 'flex',
          gap: 24,
          borderBottom: '1px solid var(--color-border)',
          padding: '0 12px',
        }}
      >
        {(
          [
            { key: 'layout', label: `Layout (${body.VISUALS.length})`, icon: Move },
            {
              key: 'criteria',
              label: `Global Criteria (${body.GLOBAL_CRITERIA.length})`,
              icon: Filter,
            },
            {
              key: 'drill',
              label: `Drill-through (${body.DRILL_THROUGH.length})`,
              icon: GitBranch,
            },
          ] as const
        ).map((t) => {
          const Icon = t.icon
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '10px 4px',
                border: 'none',
                background: 'transparent',
                borderBottom:
                  tab === t.key
                    ? '2px solid var(--color-accent)'
                    : '2px solid transparent',
                fontSize: 15,
                fontWeight: tab === t.key ? 600 : 500,
                color:
                  tab === t.key
                    ? 'var(--color-text)'
                    : 'var(--color-text-secondary)',
                cursor: 'pointer',
              }}
            >
              <Icon size={13} />
              {t.label}
            </button>
          )
        })}
      </div>

      {/* Body — fixed chrome above; each pane below scrolls internally. */}
      <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {tab === 'layout' && (
          <LayoutTab
            project={projectName}
            body={body}
            projectVisuals={projectVisuals}
            projectReports={projectReports}
            visualContents={visualContents}
            selectedVisual={selectedVisual}
            selectedIds={selectedIds}
            onSelectVisual={handleSelectVisual}
            onAddVisual={handleAddVisual}
            onDeleteVisual={handleDeleteVisual}
            onFieldChange={handleVisualFieldChange}
            onEditVisual={(objName) => setEditVisualObj(objName)}
            onPositionChange={handleVisualPositionChange}
            onMoveTiles={handleMoveTiles}
            onSetBinding={handleSetBinding}
          />
        )}
        {tab === 'criteria' && (
          <CriteriaTab
            body={body}
            project={projectName}
            projectVars={projectVars}
            selectedCriterion={selectedCriterion}
            selectedIdx={selectedCriterionIdx}
            onSelectCriterion={setSelectedCriterionIdx}
            onAdd={handleAddCriterion}
            onDelete={handleDeleteCriterion}
            onChange={handleCriterionChange}
            onToggleBroadcast={handleToggleBroadcast}
          />
        )}
        {tab === 'drill' && (
          <DrillTab
            body={body}
            project={projectName}
            projectVisuals={projectVisuals}
            projectDashboards={projectDashboards}
            selectedDrill={selectedDrill}
            selectedIdx={selectedDrillIdx}
            onSelectDrill={setSelectedDrillIdx}
            onAdd={handleAddDrill}
            onDelete={handleDeleteDrill}
            onFieldChange={handleDrillFieldChange}
            onMappingChange={handleDrillMappingChange}
            onAddMapping={handleAddDrillMapping}
            onDeleteMapping={handleDeleteDrillMapping}
          />
        )}
      </div>

      {propsOpen && (
        <DashboardPropertiesModal
          body={body}
          objectName={objectName}
          onHeaderChange={handleHeaderChange}
          onClose={() => setPropsOpen(false)}
        />
      )}

      <DiscardChangesModal
        open={showDiscardModal}
        onCancel={cancelDiscard}
        onDiscard={confirmDiscard}
        onSave={handleSaveAndContinue}
        saving={saving}
      />

      <DashboardPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        projectName={projectName}
        objectName={objectName}
        model={model}
      />

      {/* Edit-visual overlay — opens the FULL Visual designer for a tile's
          referenced visual without leaving the dashboard (same pattern as the
          Visual designer's in-place schema editor). On close, the tile's cached
          preview + loaded params are dropped so edits reflect immediately. */}
      {editVisualObj && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1100, background: 'var(--color-bg)',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '6px 12px', borderBottom: '1px solid var(--color-border)',
            background: 'var(--color-bg-secondary)',
          }}>
            <div className="flex items-center gap-2" style={{ fontSize: 14, fontWeight: 600 }}>
              <Pencil size={15} style={{ color: 'var(--color-accent)' }} />
              Editing visual · {editVisualObj}
            </div>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                const obj = editVisualObj
                setEditVisualObj(null)
                if (obj) {
                  // Drop the cached mini-preview + the loaded tile content so
                  // the layout tile re-renders the edited visual and the
                  // inspector re-reads its (possibly changed) params.
                  bustPreview(projectName, 'V', obj)
                  setVisualContents((prev) => {
                    const next = { ...prev }
                    for (const key of Object.keys(next)) {
                      if (visualRefToObjName(key) === obj) delete next[key]
                    }
                    return next
                  })
                }
              }}
              title="Close the visual editor and return to the dashboard"
            >
              <X size={14} /> Done
            </button>
          </div>
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <VisualDesignerPage
              key={`${projectName}:${editVisualObj}`}
              embedded
              embedProject={projectName}
              embedObjName={editVisualObj}
            />
          </div>
        </div>
      )}
    </div>
  )
}

// ── Layout tab ──────────────────────────────────────────────────
// Single interactive 12-column grid (tiles drag to reposition + corner /
// edge handles to resize) with a search box that highlights matching
// tiles. The right panel hosts the dashboard-level properties when no
// tile is selected and the tile inspector (with Delete) when one is.
//
// The previous design split tiles across a left list and a centre grid;
// that duplication wasted space — every tile lived twice on screen.

const ROW_HEIGHT = 60          // px per grid row (used by drag-resize math)
const GRID_GAP = 6             // px between cells
const MIN_TILE_UNITS = 0.5     // smallest tile width/height, in layout units

const RIGHT_PANEL_MIN = 240
const RIGHT_PANEL_MAX = 560
const RIGHT_PANEL_DEFAULT = 340

function LayoutTab({
  project, body, projectVisuals, projectReports, visualContents, selectedVisual, selectedIds,
  onSelectVisual, onAddVisual, onDeleteVisual,
  onFieldChange, onEditVisual, onPositionChange, onMoveTiles, onSetBinding,
}: {
  project: string
  body: DashboardBody
  projectVisuals: ProjectVisualSummary[]
  projectReports: ProjectReportSummary[]
  visualContents: Record<string, unknown | null>
  selectedVisual: DashboardVisual | null
  selectedIds: ReadonlySet<string>
  onSelectVisual: (
    id: string | null, mode?: 'single' | 'toggle' | 'primary'
  ) => void
  onAddVisual: () => void
  onDeleteVisual: (id: string) => void
  onFieldChange: <K extends keyof DashboardVisual>(
    id: string, field: K, value: DashboardVisual[K]
  ) => void
  onEditVisual: (objName: string) => void
  onPositionChange: (
    id: string, field: keyof DashboardVisual['POSITION'], value: number
  ) => void
  onMoveTiles: (moves: Array<{ id: string; X: number; Y: number }>) => void
  onSetBinding: (
    visualId: string, visualParam: string, binding: DashboardCriterionBinding | null
  ) => void
}) {
  const [search, setSearch] = useState('')
  const [findOpen, setFindOpen] = useState(false)
  // Right-panel width — drag the splitter on its left edge to resize.
  const [rightWidth, setRightWidth] = useState(RIGHT_PANEL_DEFAULT)
  const handleRightResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    const startX = e.pageX
    const startWidth = rightWidth
    const onMove = (ev: MouseEvent) => {
      const delta = startX - ev.pageX
      setRightWidth(
        Math.max(RIGHT_PANEL_MIN, Math.min(RIGHT_PANEL_MAX, startWidth + delta))
      )
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [rightWidth])

  // Tiles whose VISUAL_ID or VISUAL_NAME contain the search text (case-
  // insensitive, partial match), preserved in document order so prev/next
  // navigation feels predictable.
  const matchList = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return [] as string[]   // empty query => no "active" matches
    const ids: string[] = []
    for (const v of body.VISUALS) {
      if (
        v.VISUAL_ID.toLowerCase().includes(q)
        || (v.VISUAL_NAME || '').toLowerCase().includes(q)
      ) {
        ids.push(v.VISUAL_ID)
      }
    }
    return ids
  }, [search, body.VISUALS])
  const matches = useMemo(() => new Set(matchList), [matchList])
  const isSearching = search.trim().length > 0

  // Active position within matchList — Enter / ↓ buttons advance; ↑ goes
  // back. Reset to -1 whenever the matches change so the next Enter lands
  // on the first match instead of jumping mid-list.
  const [matchIndex, setMatchIndex] = useState(-1)
  useEffect(() => { setMatchIndex(-1) }, [matchList])

  // Per-tile DOM refs so we can scroll the active match into view.
  const tileRefs = useRef<Map<string, HTMLDivElement>>(new Map())
  const registerTileRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) tileRefs.current.set(id, el)
    else tileRefs.current.delete(id)
  }, [])

  // Scroll to a newly-added tile. We watch the VISUALS count and scroll the
  // currently-selected tile into view whenever the count grows — handleAddVisual
  // selects the new tile, so this catches "Add visual" without coupling the
  // parent to internal LayoutTab refs. requestAnimationFrame defers the lookup
  // to after the new tile mounts.
  const prevCountRef = useRef(body.VISUALS.length)
  useEffect(() => {
    const prev = prevCountRef.current
    const curr = body.VISUALS.length
    prevCountRef.current = curr
    if (curr > prev && selectedVisual) {
      const id = selectedVisual.VISUAL_ID
      requestAnimationFrame(() => {
        const el = tileRefs.current.get(id)
        el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
      })
    }
  }, [body.VISUALS.length, selectedVisual?.VISUAL_ID])

  const goToMatch = useCallback((idx: number) => {
    if (matchList.length === 0) return
    const wrapped = ((idx % matchList.length) + matchList.length) % matchList.length
    setMatchIndex(wrapped)
    const id = matchList[wrapped]
    onSelectVisual(id)
    // Defer the scroll so the just-mounted/just-styled selected node is
    // measured after the layout settles.
    requestAnimationFrame(() => {
      const el = tileRefs.current.get(id)
      el?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' })
    })
  }, [matchList, onSelectVisual])

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setSearch('')
      setFindOpen(false)
      return
    }
    if (matchList.length === 0) return
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault()
      goToMatch(matchIndex < 0 ? 0 : matchIndex + 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      goToMatch(matchIndex < 0 ? matchList.length - 1 : matchIndex - 1)
    }
  }

  return (
    <>
      <div style={{
        flex: 1,
        background: 'var(--color-bg)',
        display: 'flex', flexDirection: 'column',
        position: 'relative',
        minWidth: 0,
      }}>
        {/* Toolbar — pinned outside the scroll container so it doesn't get
            pushed away when scrollIntoView jumps to a tile. */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px 8px',
          flexShrink: 0,
        }}>
          <span style={{
            fontSize: 14, color: 'var(--color-text-faint)',
          }}>
            {body.VISUALS.length} visual{body.VISUALS.length === 1 ? '' : 's'}
            {selectedIds.size > 1 && ` · ${selectedIds.size} selected — drag any to move the group`}
            {selectedIds.size <= 1 && ' · Ctrl/⌘-click to select several tiles'}
          </span>
          <div style={{ flex: 1 }} />
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => setFindOpen((v) => !v)}
            title="Find visuals (search by id or name)"
            style={{
              padding: '4px 6px',
              color: findOpen ? 'var(--color-accent)' : undefined,
            }}
          >
            <Search size={14} />
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={onAddVisual}
            title="Add a visual to this dashboard"
          >
            <Plus size={13} /> Add visual
          </button>
        </div>

        {/* Scrollable area + floating find bar share a relative wrapper so
            the bar anchors over the grid, never the toolbar. Only the inner
            div scrolls, keeping the toolbar and find bar accessible while
            jumping between matches. */}
        <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
          <div style={{
            position: 'absolute', inset: 0, overflowY: 'auto',
            padding: '0 16px 16px',
          }}>
            {body.VISUALS.length === 0 ? (
              <div style={{
                padding: '48px 16px', textAlign: 'center',
                border: '1.5px dashed var(--color-border-strong)', borderRadius: 11,
                color: 'var(--color-text-secondary)', fontSize: 15,
              }}>
                No visuals yet. Click <strong>Add visual</strong> to add one.
              </div>
            ) : (
              <InteractiveGrid
                project={project}
                tiles={body.VISUALS}
                matches={matches}
                isSearching={isSearching}
                selectedIds={selectedIds}
                onSelect={onSelectVisual}
                onPositionChange={onPositionChange}
                onMoveTiles={onMoveTiles}
                registerTileRef={registerTileRef}
                visualContents={visualContents}
                globalCriteria={body.GLOBAL_CRITERIA}
              />
            )}
          </div>

          {findOpen && (
            <FindBar
              search={search}
              setSearch={setSearch}
              onKeyDown={onSearchKeyDown}
              matchCount={matchList.length}
              matchIndex={matchIndex}
              isSearching={isSearching}
              onPrev={() =>
                goToMatch(matchIndex < 0 ? matchList.length - 1 : matchIndex - 1)
              }
              onNext={() => goToMatch(matchIndex < 0 ? 0 : matchIndex + 1)}
              onClose={() => {
                setFindOpen(false)
                setSearch('')
              }}
            />
          )}
        </div>
      </div>

      {/* Splitter — drag to resize the right panel. */}
      <div
        onMouseDown={handleRightResize}
        title="Drag to resize"
        style={panelResizerStyle}
      >
        <div style={panelResizerLine} />
      </div>

      {/* Right panel — tile inspector when a tile is selected, otherwise
          a hint pointing the author to the gear icon for dashboard-level
          properties. */}
      <div style={{
        width: rightWidth, flexShrink: 0,
        borderLeft: '1px solid var(--color-border)',
        background: 'var(--color-bg-secondary)',
        display: 'flex', flexDirection: 'column',
        minHeight: 0,
      }}>
        <div style={{ padding: '14px 16px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {selectedVisual ? (
            <TileInspector
              tile={selectedVisual}
              projectVisuals={projectVisuals}
              projectReports={projectReports}
              visualContent={visualContents[tileContentKey(selectedVisual)] ?? undefined}
              globalCriteria={body.GLOBAL_CRITERIA}
              onFieldChange={onFieldChange}
              onEditVisual={onEditVisual}
              onPositionChange={onPositionChange}
              onSetBinding={onSetBinding}
              onDelete={() => onDeleteVisual(selectedVisual.VISUAL_ID)}
              onClose={() => onSelectVisual(null)}
            />
          ) : (
            <div style={{
              padding: '40px 16px', textAlign: 'center',
              color: 'var(--color-text-secondary)',
              lineHeight: 1.5,
            }}>
              <LayoutDashboard
                size={26}
                style={{ opacity: 0.45, marginBottom: 8 }}
              />
              <div style={{
                fontWeight: 600, fontSize: 14.5,
                color: 'var(--color-text)', marginBottom: 4,
              }}>
                Select a tile
              </div>
              <div style={{ fontSize: 14.5 }}>
                Click a tile on the grid to edit its visual, size and bindings.
              </div>
              <div style={{ marginTop: 12, fontSize: 14.5, color: 'var(--color-text-faint)' }}>
                Use the <Settings size={11} style={{ display: 'inline', verticalAlign: '-1px' }} /> icon
                next to the title to edit dashboard-level properties.
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const panelResizerStyle: React.CSSProperties = {
  width: 6,
  cursor: 'col-resize',
  flexShrink: 0,
  position: 'relative',
  background: 'transparent',
}
const panelResizerLine: React.CSSProperties = {
  position: 'absolute', left: 2, top: 0, bottom: 0, width: 1,
  background: 'var(--color-border)',
}

// ─── Floating "find" bar (opened via the search icon in the layout toolbar) ──
// Word-processor-style: anchored top-right of the grid area, autofocuses the
// input, and exposes prev/next + an "X of N" counter. It floats above the
// grid so scrolling matches into view doesn't push the bar offscreen.

function FindBar({
  search, setSearch, onKeyDown,
  matchCount, matchIndex, isSearching,
  onPrev, onNext, onClose,
}: {
  search: string
  setSearch: (s: string) => void
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void
  matchCount: number
  matchIndex: number
  isSearching: boolean
  onPrev: () => void
  onNext: () => void
  onClose: () => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])
  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        right: 16,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '6px 6px 6px 10px',
        background: 'var(--color-raised)',
        border: '1px solid var(--color-border-strong)',
        borderRadius: 8,
        boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
      }}
    >
      <Search size={13} style={{ color: 'var(--color-text-secondary)' }} />
      <input
        ref={inputRef}
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Find visual…"
        style={{
          width: 220,
          padding: '4px 6px',
          fontSize: 15.5,
          border: 'none',
          background: 'transparent',
          color: 'var(--color-text)',
          outline: 'none',
        }}
      />
      <span style={{
        fontSize: 14,
        color: 'var(--color-text-secondary)',
        minWidth: 70,
        textAlign: 'right',
        paddingRight: 4,
      }}>
        {!isSearching
          ? ''
          : matchCount === 0
            ? 'No matches'
            : matchIndex < 0
              ? `${matchCount} match${matchCount === 1 ? '' : 'es'}`
              : `${matchIndex + 1} of ${matchCount}`}
      </span>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={onPrev}
        disabled={matchCount === 0}
        title="Previous match (↑)"
        style={{ padding: '3px 5px' }}
      >
        <ChevronUp size={13} />
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={onNext}
        disabled={matchCount === 0}
        title="Next match (Enter / ↓)"
        style={{ padding: '3px 5px' }}
      >
        <ChevronDown size={13} />
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={onClose}
        title="Close (Esc)"
        style={{ padding: '3px 5px' }}
      >
        <X size={13} />
      </button>
    </div>
  )
}

// ─── Dashboard properties modal (opened via the gear icon next to title) ─

function DashboardPropertiesModal({
  body, objectName, onHeaderChange, onClose,
}: {
  body: DashboardBody
  objectName: string
  onHeaderChange: <K extends keyof DashboardBody>(
    field: K, value: DashboardBody[K]
  ) => void
  onClose: () => void
}) {
  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          background: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border)',
          borderRadius: 16,
          width: 560,
          maxWidth: '92vw',
          maxHeight: '86vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px',
          borderBottom: '1px solid var(--color-border)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            fontWeight: 700, fontSize: 17,
          }}>
            <Settings size={16} style={{ color: 'var(--color-accent)' }} />
            Dashboard properties
          </div>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            title="Close"
            style={{ padding: '4px 6px' }}
          >
            <X size={14} />
          </button>
        </div>
        <div style={{ padding: '14px 18px', minHeight: 0, overflowY: 'auto' }}>
          <DashboardProperties
            body={body}
            objectName={objectName}
            onHeaderChange={onHeaderChange}
          />
        </div>
        <div style={{
          display: 'flex', justifyContent: 'flex-end',
          padding: '12px 18px',
          borderTop: '1px solid var(--color-border)',
        }}>
          {/* Edits apply live via the header patch — Done just closes. */}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={onClose}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Theme colour (DASHBOARD.THEME_COLOR) ─────────────────────────────
//
// 'Default' = the application's selected colour. The list is data
// (visual-engine dashboardThemeColors.json); 'Custom' stores a '#rrggbb'.

const CUSTOM_THEME_COLOR = '__custom__'

function DashboardThemeColorPicker({ value, onChange }: {
  value: string | undefined
  onChange: (v: string) => void
}) {
  const appColor = useProjectStore((s) => s.currentProject?.application.theme_color) || ''
  const current = normalizeDashboardThemeColor(value)
  const isCustom = current.startsWith('#')
  const swatch = isCustom
    ? current
    : DASHBOARD_THEME_COLORS.find((c) => c.id === current)?.color || appColor || 'var(--color-accent)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        aria-hidden="true"
        style={{
          width: 28, height: 28, flexShrink: 0, borderRadius: 6, background: swatch,
          border: '1px solid var(--color-border)',
        }}
      />
      <select
        value={isCustom ? CUSTOM_THEME_COLOR : current}
        onChange={(e) => onChange(e.target.value === CUSTOM_THEME_COLOR
          ? (isCustom ? current : (appColor && /^#/.test(appColor) ? appColor : '#1565c0'))
          : e.target.value)}
        aria-label="Theme colour"
        style={{ ...inputStyle, flex: 1, minWidth: 0 }}
      >
        {DASHBOARD_THEME_COLORS.map((c) => (
          <option key={c.id} value={c.id}>{c.label}</option>
        ))}
        <option value={CUSTOM_THEME_COLOR}>Custom colour…</option>
      </select>
      {isCustom && (
        <input
          type="color"
          value={current}
          onChange={(e) => onChange(e.target.value)}
          aria-label="Custom theme colour"
          style={{ width: 36, height: 30, padding: 0, border: '1px solid var(--color-border)', borderRadius: 6, background: 'none', cursor: 'pointer' }}
        />
      )}
      {current !== DASHBOARD_THEME_DEFAULT && (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => onChange(DASHBOARD_THEME_DEFAULT)}
          title="Use the application's colour"
        >
          Reset
        </button>
      )}
    </div>
  )
}

// ─── Dashboard properties (right panel when no tile is selected) ─────

function DashboardProperties({
  body, objectName, onHeaderChange,
}: {
  body: DashboardBody
  objectName: string
  onHeaderChange: <K extends keyof DashboardBody>(
    field: K, value: DashboardBody[K]
  ) => void
}) {
  return (
    <>
      <SectionHeader>Dashboard properties</SectionHeader>
      <div style={{ ...hintStyle, marginBottom: 10 }}>
        Identifiers below are derived from the dashboard's <code>obj_name</code>.
        Rename via the prototype / app generator — that flow updates the file
        name and DASHBOARD_ID together.
      </div>

      <Field label="Object Name">
        <input
          type="text"
          value={objectName}
          disabled
          style={{
            ...inputStyle, fontFamily: MONO_FONT,
            background: 'var(--color-surface-2)',
            color: 'var(--color-text-secondary)',
          }}
        />
      </Field>
      <Field label="Dashboard ID (auto-tracks obj_name)">
        <input
          type="text"
          value={body.DASHBOARD_ID}
          disabled
          style={{
            ...inputStyle, fontFamily: MONO_FONT,
            background: 'var(--color-surface-2)',
            color: 'var(--color-text-secondary)',
          }}
        />
      </Field>
      <Field label="Type">
        <input
          type="text"
          value={`${body.DASHBOARD_TYPE || 'S'} — Standard`}
          disabled
          style={{
            ...inputStyle,
            background: 'var(--color-surface-2)',
            color: 'var(--color-text-secondary)',
          }}
        />
      </Field>

      <Field label="Name">
        <input
          type="text"
          value={body.DASHBOARD_NAME}
          onChange={(e) => onHeaderChange('DASHBOARD_NAME', e.target.value)}
          placeholder="Sales Performance Overview"
          style={inputStyle}
        />
      </Field>
      <Field label="Description">
        <textarea
          value={body.DASHBOARD_DESCR}
          onChange={(e) => onHeaderChange('DASHBOARD_DESCR', e.target.value)}
          placeholder="Consolidated sales KPIs and trend analysis"
          rows={4}
          style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
        />
        <div style={{ ...hintStyle, marginTop: 4 }}>
          Shown under the dashboard's name. Leave it empty and the line is left out.
        </div>
      </Field>
      <Field label="Theme colour">
        <DashboardThemeColorPicker
          value={body.THEME_COLOR}
          onChange={(v) => onHeaderChange('THEME_COLOR', v)}
        />
      </Field>
      <Field label="Refresh interval (seconds — 0 disables auto-refresh)">
        <input
          type="number"
          min={0}
          value={body.REFRESH_INTERVAL_SECONDS}
          onChange={(e) =>
            onHeaderChange(
              'REFRESH_INTERVAL_SECONDS',
              Math.max(0, Math.round(Number(e.target.value) || 0))
            )
          }
          style={inputStyle}
        />
      </Field>
      {/* Created By / Created Date sit side-by-side (spec §13). */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <Field label="Created By">
          <input
            type="text"
            value={body.CREATED_BY}
            onChange={(e) => onHeaderChange('CREATED_BY', e.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label="Created Date">
          <input
            type="date"
            value={body.CREATED_DATE}
            onChange={(e) => onHeaderChange('CREATED_DATE', e.target.value)}
            style={inputStyle}
          />
        </Field>
      </div>
    </>
  )
}

// ─── Tile inspector (right panel when a tile is selected) ────────────

function TileInspector({
  tile, projectVisuals, projectReports, visualContent, globalCriteria,
  onFieldChange, onEditVisual, onPositionChange, onSetBinding, onDelete, onClose,
}: {
  tile: DashboardVisual
  projectVisuals: ProjectVisualSummary[]
  projectReports: ProjectReportSummary[]
  visualContent: unknown | undefined
  globalCriteria: DashboardGlobalCriterion[]
  onFieldChange: <K extends keyof DashboardVisual>(
    id: string, field: K, value: DashboardVisual[K]
  ) => void
  onEditVisual: (objName: string) => void
  onPositionChange: (
    id: string, field: keyof DashboardVisual['POSITION'], value: number
  ) => void
  onSetBinding: (
    visualId: string, visualParam: string, binding: DashboardCriterionBinding | null
  ) => void
  onDelete: () => void
  onClose: () => void
}) {
  const visualParams = useMemo(
    () => (visualContent ? extractVisualParams(visualContent) : []),
    [visualContent]
  )
  const bindingByParam = useMemo(() => {
    const m = new Map<string, DashboardCriterionBinding>()
    for (const b of tile.CRITERIA_BINDINGS || []) m.set(b.VISUAL_PARAM, b)
    return m
  }, [tile.CRITERIA_BINDINGS])
  const isReport = tile.TILE_KIND === 'report'
  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6,
        marginBottom: 10, paddingBottom: 8,
        borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, color: 'var(--color-text-faint)',
            fontFamily: MONO_FONT,
          }}>
            {tile.VISUAL_ID}
          </div>
          <div style={{
            fontSize: 16, fontWeight: 700,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {tile.VISUAL_NAME}
          </div>
        </div>
        <button
          className="btn btn-ghost btn-sm"
          onClick={onDelete}
          title="Delete this tile from the dashboard"
          style={{ color: 'var(--color-error)', padding: '4px 6px' }}
        >
          <Trash2 size={13} />
          <span style={{ fontSize: 13.5 }}>Delete</span>
        </button>
        <button
          className="btn btn-ghost btn-sm"
          onClick={onClose}
          title="Close inspector"
          style={{ padding: '4px' }}
        >
          ×
        </button>
      </div>

      <Field label="Visual ID (unique within dashboard)">
        <input
          type="text"
          value={tile.VISUAL_ID}
          onChange={(e) =>
            onFieldChange(
              tile.VISUAL_ID, 'VISUAL_ID',
              e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '_')
            )
          }
          style={{ ...inputStyle, fontFamily: MONO_FONT, fontSize: 15 }}
        />
      </Field>
      <Field label="Tile Title">
        <input
          type="text"
          value={tile.VISUAL_NAME}
          onChange={(e) => onFieldChange(tile.VISUAL_ID, 'VISUAL_NAME', e.target.value)}
          style={inputStyle}
        />
      </Field>
      <Field label="Content">
        <select
          value={isReport ? 'report' : 'visual'}
          onChange={(e) =>
            onFieldChange(
              tile.VISUAL_ID, 'TILE_KIND',
              e.target.value === 'report' ? 'report' : undefined
            )
          }
          style={selectStyle}
        >
          <option value="visual">Visual (chart / table)</option>
          <option value="report">Report (Jasper)</option>
        </select>
      </Field>
      <Field label="Header">
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 15 }}>
          <input
            type="checkbox"
            checked={tile.SHOW_HEADER !== false}
            onChange={(e) => onFieldChange(tile.VISUAL_ID, 'SHOW_HEADER', e.target.checked)}
            style={{ cursor: 'pointer' }}
          />
          Show title bar on this tile
        </label>
        <div style={hintStyle}>
          Off = the visual renders full-bleed with no title bar — exactly how
          the tile appears at runtime. (Underlying data stays reachable by
          double-clicking a point.)
        </div>
      </Field>
      {!isReport && (
        <Field label="Visual Config">
          <ObjectPickerField
            value={tile.VISUAL_CONFIG_REF || ''}
            onChange={(filename) =>
              onFieldChange(tile.VISUAL_ID, 'VISUAL_CONFIG_REF', filename)
            }
            catalog={projectVisuals.map((v) => ({
              obj_name: v.filename,
              title: v.obj_name,
              hint: v.visual_type || v.schema_name || 'visual',
            }))}
            typeLabel="visual"
            placeholder="Pick a visual or leave blank for a placeholder…"
            emptyHint="No visuals in this project yet. Build one in the Visual designer first, or use a custom filename."
          />
          <div style={hintStyle}>
            File at <code>Metadata_Model/visual_models/&lt;file&gt;.json</code>.
          </div>
          {tile.VISUAL_CONFIG_REF && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => onEditVisual(visualRefToObjName(tile.VISUAL_CONFIG_REF || ''))}
              title="Open this visual in the Visual designer to change its chart, data source and options"
              style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <Pencil size={13} /> Edit Visual
            </button>
          )}
        </Field>
      )}
      {isReport && (
        <Field label="Report">
          <ObjectPickerField
            value={tile.REPORT_OBJ || ''}
            onChange={(objName) =>
              onFieldChange(tile.VISUAL_ID, 'REPORT_OBJ', objName)
            }
            catalog={projectReports.map((r) => ({
              obj_name: r.obj_name,
              title: r.title || r.obj_name,
              hint: r.description || 'report',
            }))}
            typeLabel="report"
            placeholder="Pick a report…"
            emptyHint="No reports in this project yet. Build one in the Report designer first, or use a custom name."
          />
          <div style={hintStyle}>
            Rendered in-tile by the shared report viewer; bindings below feed
            its prompt parameters.
          </div>
        </Field>
      )}

      <SectionHeader>Position &amp; size</SectionHeader>
      <div style={{ ...hintStyle, marginBottom: 6 }}>
        You can also drag the tile or its handles directly on the grid —
        positions snap to quarter cells (0.25). Ctrl/⌘-click selects several
        tiles to drag as a group; arrow keys nudge the selection (Shift = a
        whole cell).
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Field label="X (0–11.75)">
          <input
            type="number" min={0} max={11.75} step={0.25}
            value={tile.POSITION.X}
            onChange={(e) =>
              onPositionChange(
                tile.VISUAL_ID, 'X',
                Math.max(0, Math.min(11.75, Number(e.target.value) || 0))
              )
            }
            style={inputStyle}
          />
        </Field>
        <Field label="Y (0+)">
          <input
            type="number" min={0} step={0.25}
            value={tile.POSITION.Y}
            onChange={(e) =>
              onPositionChange(
                tile.VISUAL_ID, 'Y',
                Math.max(0, Number(e.target.value) || 0)
              )
            }
            style={inputStyle}
          />
        </Field>
        <Field label="Width (0.5–12)">
          <input
            type="number" min={0.5} max={12} step={0.25}
            value={tile.POSITION.WIDTH}
            onChange={(e) =>
              onPositionChange(
                tile.VISUAL_ID, 'WIDTH',
                Math.max(0.5, Math.min(12, Number(e.target.value) || 0.5))
              )
            }
            style={inputStyle}
          />
        </Field>
        <Field label="Height (0.5+)">
          <input
            type="number" min={0.5} step={0.25}
            value={tile.POSITION.HEIGHT}
            onChange={(e) =>
              onPositionChange(
                tile.VISUAL_ID, 'HEIGHT',
                Math.max(0.5, Number(e.target.value) || 0.5)
              )
            }
            style={inputStyle}
          />
        </Field>
      </div>

      <SectionHeader>Criteria bindings</SectionHeader>
      <div style={{ ...hintStyle, marginBottom: 8 }}>
        {isReport
          ? 'How each prompt parameter of this report is filled at runtime — '
            + 'wire it to a dashboard global criterion, pin it to a constant '
            + 'value, or leave it unbound (the report prompts the user as usual).'
          : 'How each variable in this visual is filled at runtime — wire it '
            + 'to a dashboard global criterion, pin it to a constant value, or '
            + 'leave it unbound (the visual prompts the user as usual).'}
      </div>
      {!(isReport ? tile.REPORT_OBJ : tile.VISUAL_CONFIG_REF) ? (
        <div style={emptyHintStyle}>
          Pick a {isReport ? 'report' : 'visual config'} above to see its variables.
        </div>
      ) : visualContent === undefined ? (
        <div style={emptyHintStyle}>Loading {isReport ? 'report' : 'visual'}…</div>
      ) : visualContent === null ? (
        <div style={emptyHintStyle}>
          Couldn't load <code>{isReport ? tile.REPORT_OBJ : tile.VISUAL_CONFIG_REF}</code>
          {' '}— its file may be missing.
        </div>
      ) : visualParams.length === 0 ? (
        <div style={emptyHintStyle}>
          This {isReport ? 'report' : 'visual'} has no prompt variables (no{' '}
          <code>?.&lt;var&gt;</code> placeholders or criteria rules).
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visualParams.map((p) => (
            <BindingRow
              key={p.name}
              param={p}
              binding={bindingByParam.get(p.name)}
              globalCriteria={globalCriteria}
              onChange={(next) => onSetBinding(tile.VISUAL_ID, p.name, next)}
            />
          ))}
        </div>
      )}
    </>
  )
}

// One row inside the Criteria bindings section. Lets the author pick the
// binding type for a single variable, then renders the relevant editor:
//   - global   → dropdown of dashboard GLOBAL_CRITERIA PARAM_NAMEs
//   - constant → free-text input typed for the variable's data type
//   - unbound  → no editor (the visual prompts at runtime)

function BindingRow({
  param, binding, globalCriteria, onChange,
}: {
  param: VisualParam
  binding: DashboardCriterionBinding | undefined
  globalCriteria: DashboardGlobalCriterion[]
  onChange: (next: DashboardCriterionBinding | null) => void
}) {
  const bindType: DashboardBindType = binding?.BIND_TYPE || 'unbound'
  const globalParam = binding?.GLOBAL_PARAM || ''
  const constantValue = binding?.CONSTANT_VALUE || ''
  const globalIsBroken =
    bindType === 'global'
    && globalParam !== ''
    && !globalCriteria.some((c) => c.PARAM_NAME === globalParam)

  const setBindType = (t: DashboardBindType) => {
    if (t === 'unbound') {
      onChange(null)
      return
    }
    if (t === 'global') {
      // Default to the first matching-name global, else the first one
      // available. Author can adjust via the dropdown.
      const match = globalCriteria.find((c) => c.PARAM_NAME === param.name)
        || globalCriteria[0]
      onChange({
        VISUAL_PARAM: param.name,
        BIND_TYPE: 'global',
        GLOBAL_PARAM: match?.PARAM_NAME || '',
      })
      return
    }
    onChange({
      VISUAL_PARAM: param.name,
      BIND_TYPE: 'constant',
      CONSTANT_VALUE: '',
    })
  }

  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 8,
        padding: '9px 10px',
        background: 'var(--color-surface-2)',
        display: 'flex', flexDirection: 'column', gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontFamily: MONO_FONT, fontSize: 13.5, fontWeight: 600 }}>
          {param.name}
        </span>
        <span style={{ fontSize: 14, color: 'var(--color-text-faint)' }}>
          {param.promptLabel} · {param.dataType} {param.operator}
        </span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <select
          value={bindType}
          onChange={(e) => setBindType(e.target.value as DashboardBindType)}
          style={{ ...selectStyle, flex: '0 0 auto', width: 120 }}
        >
          <option value="unbound">Use default</option>
          <option value="global">Global</option>
          <option value="constant">Constant</option>
        </select>
        {bindType === 'global' && (
          globalCriteria.length === 0 ? (
            <span style={{ fontSize: 14, color: 'var(--color-text-secondary)' }}>
              No global criteria yet — add some on the Global Criteria tab.
            </span>
          ) : (
            <select
              value={globalParam}
              onChange={(e) =>
                onChange({
                  VISUAL_PARAM: param.name,
                  BIND_TYPE: 'global',
                  GLOBAL_PARAM: e.target.value,
                })
              }
              style={{
                ...selectStyle,
                flex: 1,
                borderColor: globalIsBroken ? 'var(--color-error)' : undefined,
              }}
              title={globalIsBroken
                ? `"${globalParam}" is no longer a dashboard criterion`
                : undefined}
            >
              {globalIsBroken && (
                <option value={globalParam}>{globalParam} (missing)</option>
              )}
              {globalCriteria.map((c) => (
                <option key={c.PARAM_NAME} value={c.PARAM_NAME}>
                  {c.PROMPT_LABEL ? `${c.PROMPT_LABEL} (${c.PARAM_NAME})` : c.PARAM_NAME}
                </option>
              ))}
            </select>
          )
        )}
        {bindType === 'constant' && (
          <input
            type={
              param.dataType === 'date' ? 'date'
              : param.dataType === 'number' ? 'number'
              : 'text'
            }
            value={constantValue}
            onChange={(e) =>
              onChange({
                VISUAL_PARAM: param.name,
                BIND_TYPE: 'constant',
                CONSTANT_VALUE: e.target.value,
              })
            }
            placeholder={`Constant ${param.dataType} value`}
            style={{ ...inputStyle, flex: 1 }}
          />
        )}
      </div>
    </div>
  )
}

// ─── Interactive 12-col grid with drag/resize ────────────────────────
// CSS Grid handles the visual layout; pointer interactions adjust the
// underlying X/Y/W/H by snapping pixel deltas to grid cells. Tiles that
// don't match the active search are dimmed but stay clickable.

function InteractiveGrid({
  project, tiles, matches, isSearching, selectedIds, onSelect, onPositionChange,
  onMoveTiles, registerTileRef, visualContents, globalCriteria,
}: {
  project: string
  tiles: DashboardVisual[]
  matches: Set<string>
  isSearching: boolean
  selectedIds: ReadonlySet<string>
  onSelect: (id: string | null, mode?: 'single' | 'toggle' | 'primary') => void
  onPositionChange: (
    id: string, field: keyof DashboardVisual['POSITION'], value: number
  ) => void
  onMoveTiles: (moves: Array<{ id: string; X: number; Y: number }>) => void
  registerTileRef: (id: string, el: HTMLDivElement | null) => void
  visualContents: Record<string, unknown | null>
  globalCriteria: DashboardGlobalCriterion[]
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const maxRow = tiles.reduce(
    (acc, t) => Math.max(acc, t.POSITION.Y + t.POSITION.HEIGHT),
    6
  )

  // Pixel size of one layout unit (a 12th of the width / one 60px row plus
  // its gap). Recomputed lazily at pointerdown — no need to track on resize
  // since drag math runs only during interactions.
  const getUnitSize = () => {
    const w = containerRef.current?.clientWidth || 720
    const pad = Math.max(0, 8 - GRID_GAP / 2)
    return {
      unitW: Math.max(24, (w - pad * 2) / DASH_COLS),
      unitH: ROW_HEIGHT + GRID_GAP,
    }
  }

  // Snapshot of the selected tiles' positions — a group drag applies its
  // delta against this, so all members move rigidly together.
  const getGroupStart = () =>
    tiles
      .filter((t) => selectedIds.has(t.VISUAL_ID))
      .map((t) => ({ id: t.VISUAL_ID, ...t.POSITION }))

  return (
    <div
      ref={containerRef}
      style={{
        ...dashGridContainerStyle(ROW_HEIGHT, GRID_GAP, 8, { stretch: false }),
        border: '1px solid var(--color-border)',
        borderRadius: 12,
        background: 'var(--color-surface-2)',
        // 12-column grid feel (spec §9.3.1) — faint column/row rules painted
        // with repeating gradients so the snap geometry stays visible.
        backgroundImage: `
          repeating-linear-gradient(to right,
            color-mix(in srgb, var(--color-border) 55%, transparent) 0 1px,
            transparent 1px calc(100% / ${DASH_COLS})),
          repeating-linear-gradient(to bottom,
            color-mix(in srgb, var(--color-border) 55%, transparent) 0 1px,
            transparent 1px ${ROW_HEIGHT + GRID_GAP}px)
        `,
        minHeight: maxRow * (ROW_HEIGHT + GRID_GAP) + 80,
      }}
      onMouseDown={(e) => {
        // Click on the empty grid background → clear selection.
        if (e.target === e.currentTarget) onSelect(null)
      }}
    >
      {tiles.map((t) => (
        <GridTile
          key={t.VISUAL_ID}
          project={project}
          tile={t}
          // Dim tiles that don't match the active search. When the search
          // is empty every tile is fully visible.
          dimmed={isSearching && !matches.has(t.VISUAL_ID)}
          selected={selectedIds.has(t.VISUAL_ID)}
          inGroup={selectedIds.has(t.VISUAL_ID) && selectedIds.size > 1}
          onSelect={(mode) => onSelect(t.VISUAL_ID, mode)}
          onPositionChange={onPositionChange}
          onMoveTiles={onMoveTiles}
          getGroupStart={getGroupStart}
          getUnitSize={getUnitSize}
          registerTileRef={registerTileRef}
          visualContent={visualContents[tileContentKey(t)] ?? undefined}
          globalCriteria={globalCriteria}
        />
      ))}
    </div>
  )
}

function GridTile({
  project, tile, dimmed, selected, inGroup, onSelect, onPositionChange, onMoveTiles,
  getGroupStart, getUnitSize, registerTileRef, visualContent, globalCriteria,
}: {
  project: string
  tile: DashboardVisual
  dimmed: boolean
  selected: boolean
  inGroup: boolean
  onSelect: (mode?: 'single' | 'toggle' | 'primary') => void
  onPositionChange: (
    id: string, field: keyof DashboardVisual['POSITION'], value: number
  ) => void
  onMoveTiles: (moves: Array<{ id: string; X: number; Y: number }>) => void
  getGroupStart: () => Array<{
    id: string; X: number; Y: number; WIDTH: number; HEIGHT: number
  }>
  getUnitSize: () => { unitW: number; unitH: number }
  registerTileRef: (id: string, el: HTMLDivElement | null) => void
  visualContent: unknown | undefined
  globalCriteria: DashboardGlobalCriterion[]
}) {
  const visualParams = useMemo(
    () => (visualContent ? extractVisualParams(visualContent) : []),
    [visualContent]
  )
  const bindingByParam = useMemo(() => {
    const m = new Map<string, DashboardCriterionBinding>()
    for (const b of tile.CRITERIA_BINDINGS || []) m.set(b.VISUAL_PARAM, b)
    return m
  }, [tile.CRITERIA_BINDINGS])
  const validGlobals = useMemo(
    () => new Set(globalCriteria.map((c) => c.PARAM_NAME).filter(Boolean)),
    [globalCriteria]
  )
  // The saved visual object this tile references — drives the live preview.
  const visualObjName = useMemo(
    () => (tile.TILE_KIND === 'report' ? '' : visualRefToObjName(tile.VISUAL_CONFIG_REF || '')),
    [tile.TILE_KIND, tile.VISUAL_CONFIG_REF]
  )
  // Whether this tile's title header renders at runtime (absent = shown).
  const headerShown = tile.SHOW_HEADER !== false
  const w = Math.max(DASH_STEP, tile.POSITION.WIDTH)
  const h = Math.max(DASH_STEP, tile.POSITION.HEIGHT)

  // Generic drag handler used by both the body (move) and the resize
  // handles (right/bottom/corner). `axis` controls which fields move.
  // Deltas snap to quarter-cell steps for fine-grained control; a body
  // drag on a multi-selected tile moves the whole group rigidly.
  const startDrag = (
    e: React.MouseEvent,
    axis: 'move' | 'x' | 'y' | 'xy',
  ) => {
    e.preventDefault()
    e.stopPropagation()
    onSelect(inGroup ? 'primary' : 'single')
    const { unitW, unitH } = getUnitSize()
    const startX = e.clientX
    const startY = e.clientY
    const startPos = { ...tile.POSITION }
    const group = axis === 'move' && inGroup ? getGroupStart() : null
    const groupMinX = group ? Math.min(...group.map((g) => g.X)) : 0
    const groupMaxRight = group
      ? Math.max(...group.map((g) => g.X + g.WIDTH))
      : 0
    const groupMinY = group ? Math.min(...group.map((g) => g.Y)) : 0

    let lastDx = 0
    let lastDy = 0
    const onMove = (ev: MouseEvent) => {
      const dxCells = dashSnap((ev.clientX - startX) / unitW)
      const dyCells = dashSnap((ev.clientY - startY) / unitH)
      if (dxCells === lastDx && dyCells === lastDy) return
      lastDx = dxCells
      lastDy = dyCells

      if (axis === 'move') {
        if (group) {
          // Clamp the delta so the whole group stays on the canvas, then
          // move every member by the same amount.
          const cdx = Math.max(
            -groupMinX, Math.min(DASH_COLS - groupMaxRight, dxCells)
          )
          const cdy = Math.max(-groupMinY, dyCells)
          onMoveTiles(group.map((g) => ({
            id: g.id, X: dashSnap(g.X + cdx), Y: dashSnap(g.Y + cdy),
          })))
        } else {
          const newX = Math.max(
            0, Math.min(DASH_COLS - startPos.WIDTH, dashSnap(startPos.X + dxCells))
          )
          const newY = Math.max(0, dashSnap(startPos.Y + dyCells))
          if (newX !== tile.POSITION.X || newY !== tile.POSITION.Y) {
            onMoveTiles([{ id: tile.VISUAL_ID, X: newX, Y: newY }])
          }
        }
      }
      if (axis === 'x' || axis === 'xy') {
        const newW = Math.max(
          MIN_TILE_UNITS,
          Math.min(DASH_COLS - startPos.X, dashSnap(startPos.WIDTH + dxCells))
        )
        if (newW !== tile.POSITION.WIDTH) onPositionChange(tile.VISUAL_ID, 'WIDTH', newW)
      }
      if (axis === 'y' || axis === 'xy') {
        const newH = Math.max(MIN_TILE_UNITS, dashSnap(startPos.HEIGHT + dyCells))
        if (newH !== tile.POSITION.HEIGHT) onPositionChange(tile.VISUAL_ID, 'HEIGHT', newH)
      }
    }
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.body.style.cursor = axis === 'move'
      ? 'grabbing'
      : axis === 'x' ? 'ew-resize'
      : axis === 'y' ? 'ns-resize' : 'nwse-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  return (
    <div
      ref={(el) => registerTileRef(tile.VISUAL_ID, el)}
      onMouseDown={(e) => {
        if (e.button !== 0) return
        // Ctrl/Cmd/Shift-click toggles the tile in a multi-selection
        // instead of starting a drag.
        if (e.ctrlKey || e.metaKey || e.shiftKey) {
          e.preventDefault()
          e.stopPropagation()
          onSelect('toggle')
          return
        }
        startDrag(e, 'move')
      }}
      onClick={(e) => { e.stopPropagation() }}
      style={{
        ...dashTileStyle(tile.POSITION, GRID_GAP),
        position: 'relative',
        border: selected
          ? '2px solid var(--color-accent)'
          : '1px solid var(--color-border)',
        borderRadius: 8,
        background: selected ? accentWeak : 'var(--color-bg-secondary)',
        boxShadow: selected ? '0 0 0 3px var(--color-accent-weak)' : undefined,
        padding: 0,
        cursor: 'grab',
        overflow: 'hidden',
        opacity: dimmed ? 0.35 : 1,
        transition: 'opacity 0.15s',
        userSelect: 'none',
      }}
    >
      {/* Full-bleed visual — fills the tile edge-to-edge, so the design view
          matches the runtime (no chrome steals the chart's space). The header
          and footer below are TRANSLUCENT overlays: design-only affordances
          that float over the chart. The footer never exists at runtime; the
          header is real ONLY when SHOW_HEADER is on (flagged below). */}
      <div style={{ position: 'absolute', inset: 0, background: 'var(--color-bg)' }}>
        {tile.TILE_KIND === 'report' ? (
          <TilePlaceholder>
            {tile.REPORT_OBJ ? `Report · ${tile.REPORT_OBJ}` : '(no report configured)'}
          </TilePlaceholder>
        ) : visualObjName ? (
          <PreviewThumbnail
            key={visualObjName}
            project={project}
            objName={visualObjName}
            kind="V"
            height={Math.max(40, h * ROW_HEIGHT)}
          />
        ) : (
          <TilePlaceholder>(no visual configured)</TilePlaceholder>
        )}
      </div>

      {/* Header overlay (translucent) — name + size for design identification.
          When this tile's header is turned OFF it's struck through with an
          eye-off badge, flagging that it won't render at runtime. pointer-events
          off so it never blocks tile drag/resize. */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, pointerEvents: 'none',
        display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px',
        background: 'color-mix(in srgb, var(--color-bg-secondary) 76%, transparent)',
        backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
        borderBottom: '1px solid color-mix(in srgb, var(--color-border) 55%, transparent)',
        opacity: headerShown ? 1 : 0.65,
      }}>
        <span title={tile.VISUAL_ID} style={{
          flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 700,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          textDecoration: headerShown ? 'none' : 'line-through',
        }}>
          {tile.VISUAL_NAME || tile.VISUAL_ID}
        </span>
        {!headerShown && (
          <EyeOff size={12} style={{ flexShrink: 0, color: 'var(--color-text-faint)' }} />
        )}
        <span style={{
          flexShrink: 0, fontSize: 13, fontFamily: MONO_FONT, color: 'var(--color-text-faint)',
        }}>
          {w}×{h}
        </span>
      </div>

      {/* Filter footer overlay (translucent) — the criteria-wiring summary.
          Design-only (it never appears at runtime), so it floats over the chart
          rather than taking its space. Broken global bindings surface in red. */}
      {visualParams.length > 0 && (() => {
        let bound = 0
        let broken = 0
        for (const p of visualParams) {
          const b = bindingByParam.get(p.name)
          const t: DashboardBindType = b?.BIND_TYPE || 'unbound'
          if (t !== 'unbound') bound += 1
          if (t === 'global' && !!b?.GLOBAL_PARAM && !validGlobals.has(b.GLOBAL_PARAM)) broken += 1
        }
        return (
          <div
            title={visualParams
              .map((p) => {
                const b = bindingByParam.get(p.name)
                const t: DashboardBindType = b?.BIND_TYPE || 'unbound'
                const val = t === 'global'
                  ? `← @${b?.GLOBAL_PARAM || '?'}`
                  : t === 'constant'
                    ? `= ${formatConst(b?.CONSTANT_VALUE || '')}`
                    : '(default)'
                return `${p.name} ${val}`
              })
              .join('\n')}
            style={{
              position: 'absolute', bottom: 0, left: 0, right: 0, pointerEvents: 'none',
              display: 'flex', alignItems: 'center', gap: 6, padding: '3px 8px',
              background: 'color-mix(in srgb, var(--color-bg-secondary) 76%, transparent)',
              backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
              borderTop: '1px solid color-mix(in srgb, var(--color-border) 55%, transparent)',
              fontSize: 13, fontFamily: MONO_FONT, color: 'var(--color-text-secondary)',
              overflow: 'hidden', whiteSpace: 'nowrap',
            }}
          >
            <Filter size={11} style={{ flexShrink: 0, opacity: 0.7 }} />
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {bound}/{visualParams.length} filter{visualParams.length === 1 ? '' : 's'} wired
            </span>
            {broken > 0 && (
              <span style={{ flexShrink: 0, color: 'var(--color-error)', fontWeight: 600 }}>
                · {broken} broken
              </span>
            )}
          </div>
        )
      })()}

      {/* Resize handles — only visible while selected to keep the grid
          uncluttered. The corner is the most discoverable; right/bottom
          edges allow constrained one-axis resize. */}
      {selected && (
        <>
          <div
            onMouseDown={(e) => startDrag(e, 'x')}
            title="Drag to resize width"
            style={{
              position: 'absolute', top: 0, bottom: 0, right: -3,
              width: 6, cursor: 'ew-resize', background: 'transparent',
            }}
          />
          <div
            onMouseDown={(e) => startDrag(e, 'y')}
            title="Drag to resize height"
            style={{
              position: 'absolute', left: 0, right: 0, bottom: -3,
              height: 6, cursor: 'ns-resize', background: 'transparent',
            }}
          />
          <div
            onMouseDown={(e) => startDrag(e, 'xy')}
            title="Drag to resize"
            style={{
              position: 'absolute', right: -4, bottom: -4,
              width: 12, height: 12, cursor: 'nwse-resize',
              background: 'var(--color-accent)',
              borderRadius: 2,
              boxShadow: '0 0 0 2px var(--color-bg)',
            }}
          />
        </>
      )}
    </div>
  )
}

// Compact centered label for a Layout tile with no live visual to preview
// (report tiles, or a tile whose visual reference is not yet set).
function TilePlaceholder({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
      justifyContent: 'center', textAlign: 'center', padding: 8,
      fontSize: 13, fontFamily: MONO_FONT, color: 'var(--color-text-faint)',
    }}>
      {children}
    </div>
  )
}

function formatConst(v: string): string {
  if (v === '') return '""'
  // Quote string-looking values so the constant feels literal in the
  // mini list; numbers and dates render bare.
  if (/^-?\d+(\.\d+)?$/.test(v)) return v
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v
  return `"${v}"`
}

// ── Criteria tab ────────────────────────────────────────────────
// Shows the global filter bar rules. Each rule is broadcast to a chosen
// subset of tiles via the BROADCAST_TO checkboxes — tiles not checked are
// not affected by that filter.
function CriteriaTab({
  body, project, projectVars, selectedCriterion, selectedIdx,
  onSelectCriterion, onAdd, onDelete,
  onChange, onToggleBroadcast,
}: {
  body: DashboardBody
  project: string
  projectVars: ProjectVariable[]
  selectedCriterion: DashboardGlobalCriterion | null
  selectedIdx: number | null
  onSelectCriterion: (idx: number | null) => void
  onAdd: () => void
  onDelete: (idx: number) => void
  onChange: <K extends keyof DashboardGlobalCriterion>(
    idx: number, field: K, value: DashboardGlobalCriterion[K]
  ) => void
  onToggleBroadcast: (idx: number, visualId: string) => void
}) {
  return (
    <>
      <div
        style={{
          width: 300,
          flexShrink: 0,
          minHeight: 0,
          overflowY: 'auto',
          borderRight: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
          padding: '14px 12px',
        }}
      >
        <SectionHeader
          right={
            <button
              className="btn btn-ghost btn-sm"
              onClick={onAdd}
              title="Add criterion"
              style={{
                width: 24, height: 24, padding: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid var(--color-border)', borderRadius: 6,
              }}
            >
              <Plus size={13} />
            </button>
          }
        >
          Global Criteria ({body.GLOBAL_CRITERIA.length})
        </SectionHeader>
        {body.GLOBAL_CRITERIA.length === 0 && (
          <div style={emptyHintStyle}>
            No global filters. Add one to broadcast a value to multiple tiles.
          </div>
        )}
        {body.GLOBAL_CRITERIA.map((c, i) => (
          <div
            key={i}
            onClick={() => onSelectCriterion(i)}
            style={{
              ...chipStyle,
              flexDirection: 'column',
              alignItems: 'stretch',
              border: selectedIdx === i
                ? '1px solid var(--color-accent)'
                : '1px solid var(--color-border)',
              background:
                selectedIdx === i ? accentWeak : 'var(--color-surface-2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                {c.PROMPT_LABEL || c.PARAM_NAME || '(unnamed)'}
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(i)
                }}
                title="Remove"
                style={{ padding: '2px 4px' }}
              >
                <Trash2 size={12} />
              </button>
            </div>
            <div
              style={{
                fontSize: 14,
                color: 'var(--color-text-secondary)',
                fontFamily: MONO_FONT,
              }}
            >
              {c.PARAM_NAME || '—'} · {c.DATA_TYPE} · {c.OPERATOR}
            </div>
            <div
              style={{
                fontSize: 14,
                color: 'var(--color-text-faint)',
              }}
            >
              → {c.BROADCAST_TO.length}/{body.VISUALS.length} tiles
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '16px 20px',
          background: 'var(--color-bg)',
        }}
      >
        <SectionHeader>Criterion Inspector</SectionHeader>
        {!selectedCriterion || selectedIdx == null ? (
          <div style={emptyHintStyle}>
            Select or add a criterion to edit it.
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                marginBottom: 16,
                maxWidth: 820,
              }}
            >
              <Field label="Param Name (matches visual variable)">
                <input
                  type="text"
                  value={selectedCriterion.PARAM_NAME}
                  onChange={(e) =>
                    onChange(selectedIdx, 'PARAM_NAME', e.target.value)
                  }
                  placeholder="e.g. from_date or DWH_SALES_SUM_SITE_CODE"
                  style={{ ...inputStyle, fontFamily: MONO_FONT, fontSize: 15 }}
                />
              </Field>
              <Field label="Prompt Label">
                <input
                  type="text"
                  value={selectedCriterion.PROMPT_LABEL}
                  onChange={(e) =>
                    onChange(selectedIdx, 'PROMPT_LABEL', e.target.value)
                  }
                  placeholder="e.g. From Date"
                  style={inputStyle}
                />
              </Field>
              <Field label="Data Type">
                <select
                  value={selectedCriterion.DATA_TYPE}
                  onChange={(e) =>
                    onChange(
                      selectedIdx,
                      'DATA_TYPE',
                      e.target.value as DashboardDataType
                    )
                  }
                  style={selectStyle}
                >
                  {DATA_TYPE_OPTIONS.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Operator">
                <select
                  value={selectedCriterion.OPERATOR}
                  onChange={(e) =>
                    onChange(
                      selectedIdx,
                      'OPERATOR',
                      e.target.value as DashboardOperator
                    )
                  }
                  style={selectStyle}
                >
                  {OPERATOR_OPTIONS.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Default Value">
                {selectedCriterion.OPERATOR === 'between' ? (
                  // BETWEEN default = "lo,hi" (the comma-joined pair the
                  // engine splits into the two binds) — two inputs, same as
                  // the runtime criteria bar renders it.
                  <div style={{ display: 'flex', gap: 6 }}>
                    {([0, 1] as const).map((pos) => {
                      const parts = (selectedCriterion.DEFAULT_VALUE || '').split(',')
                      const lo = (parts[0] ?? '').trim()
                      const hi = (parts[1] ?? '').trim()
                      const it = selectedCriterion.DATA_TYPE === 'date' ? 'date'
                        : selectedCriterion.DATA_TYPE === 'number' ? 'number' : 'text'
                      return (
                        <input
                          key={pos}
                          type={it}
                          value={pos === 0 ? lo : hi}
                          placeholder={pos === 0 ? 'From' : 'To'}
                          onChange={(e) => {
                            const v = e.target.value.trim()
                            const nl = pos === 0 ? v : lo
                            const nh = pos === 1 ? v : hi
                            onChange(
                              selectedIdx, 'DEFAULT_VALUE',
                              nl || nh ? `${nl},${nh}` : '',
                            )
                          }}
                          style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                        />
                      )
                    })}
                  </div>
                ) : (
                  /* Token-aware default editor (literal OR :calendar_* /
                     :user_* / :<project-var> token, resolved server-side) —
                     same control the visual designer uses. IN operators get
                     chip-style list entry (stored comma-joined). */
                  <CriteriaDefaultField
                    value={selectedCriterion.DEFAULT_VALUE || ''}
                    projectVars={projectVars}
                    chips={selectedCriterion.OPERATOR === 'in'}
                    chipInputType={selectedCriterion.DATA_TYPE === 'date' ? 'date'
                      : selectedCriterion.DATA_TYPE === 'number' ? 'number' : 'text'}
                    onChange={(val) => onChange(selectedIdx, 'DEFAULT_VALUE', val)}
                    inputStyle={inputStyle}
                    selectStyle={selectStyle}
                  />
                )}
              </Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!selectedCriterion.mandatory}
                  onChange={(e) => onChange(selectedIdx, 'mandatory', (e.target.checked || undefined) as never)}
                />
                Mandatory — the dashboard waits until this has a value
              </label>
              {selectedCriterion.mandatory && !(selectedCriterion.DEFAULT_VALUE || '').trim() && (
                <div style={{ ...emptyHintStyle, marginTop: -4 }}>
                  No default, so the dashboard opens with the criteria shown and
                  runs no tile until it is filled in.
                </div>
              )}
            </div>

            <SectionHeader>Checks</SectionHeader>
            <div style={{ padding: '0 4px 8px' }}>
              <CriteriaValidationsEditor
                project={project}
                label={selectedCriterion.PROMPT_LABEL || selectedCriterion.PARAM_NAME}
                criteriaNames={body.GLOBAL_CRITERIA
                  .map((c) => (c.PARAM_NAME || '').trim().replace(/\s+/g, '_').toLowerCase())
                  .filter(Boolean)}
                validations={selectedCriterion.validations}
                onChange={(next) => onChange(selectedIdx, 'validations', (next.length ? next : undefined) as never)}
              />
            </div>

            <SectionHeader>Lookup &amp; item change</SectionHeader>
            <div style={{ padding: '0 4px 8px' }}>
              <CriteriaLookupItemChange
                project={project}
                triggerName={(selectedCriterion.PARAM_NAME || '').trim().replace(/\s+/g, '_').toUpperCase()}
                otherParamNames={body.GLOBAL_CRITERIA
                  .filter((_, j) => j !== selectedIdx)
                  .map((c) => (c.PARAM_NAME || '').trim().replace(/\s+/g, '_').toUpperCase())
                  .filter(Boolean)}
                value={{
                  lookup: selectedCriterion.lookup,
                  lookup_filter: selectedCriterion.lookup_filter,
                  lookup_filter_rules: selectedCriterion.lookup_filter_rules,
                  lookup_sql: selectedCriterion.lookup_sql,
                  lookup_title: selectedCriterion.lookup_title,
                  lookup_sql_data_col: selectedCriterion.lookup_sql_data_col,
                  item_change: selectedCriterion.item_change,
                  options: selectedCriterion.options,
                }}
                onLookupChange={(patch) => {
                  for (const [k, v] of Object.entries(patch)) {
                    onChange(selectedIdx, k as keyof DashboardGlobalCriterion, v as never)
                  }
                }}
                onItemChange={(v) => onChange(selectedIdx, 'item_change', (v ?? null) as never)}
                onOptionsChange={(v) => onChange(selectedIdx, 'options', (v ?? []) as never)}
              />
            </div>

            <SectionHeader>
              Broadcast to ({selectedCriterion.BROADCAST_TO.length}/
              {body.VISUALS.length})
            </SectionHeader>
            {body.VISUALS.length === 0 ? (
              <div style={emptyHintStyle}>
                No tiles in this dashboard yet. Add tiles on the Visuals tab
                first.
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                  gap: 8,
                  maxWidth: 1100,
                }}
              >
                {body.VISUALS.map((v) => {
                  const checked = selectedCriterion.BROADCAST_TO.includes(
                    v.VISUAL_ID
                  )
                  return (
                    <label
                      key={v.VISUAL_ID}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 10px',
                        border: checked
                          ? '1px solid var(--color-accent)'
                          : '1px solid var(--color-border)',
                        borderRadius: 8,
                        background: checked
                          ? accentWeak
                          : 'var(--color-bg-secondary)',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          onToggleBroadcast(selectedIdx, v.VISUAL_ID)
                        }
                      />
                      <span
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 15,
                        }}
                      >
                        <div
                          style={{
                            fontFamily: MONO_FONT,
                            fontSize: 14,
                            color: 'var(--color-text-faint)',
                          }}
                        >
                          {v.VISUAL_ID}
                        </div>
                        <div
                          style={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {v.VISUAL_NAME}
                        </div>
                      </span>
                    </label>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

// ── Drill-through tab ───────────────────────────────────────────

const DRILL_TARGET_TYPE_OPTIONS: {
  value: DashboardDrillTargetType
  label: string
}[] = [
  { value: 'dashboard', label: 'Dashboard' },
  { value: 'visual', label: 'Visual' },
  { value: 'self', label: 'This dashboard (cross-filter)' },
]

// Human label for the list chip's "→ target" side.
function drillTargetLabel(d: DashboardDrillThrough): string {
  if (d.TARGET_TYPE === 'self') return '(this dashboard)'
  if (d.TARGET_TYPE === 'visual') return d.TARGET_VISUAL_ID || '(target visual)'
  return d.TARGET_DASHBOARD_ID || '(target)'
}

function DrillTab({
  body, project, projectVisuals, projectDashboards,
  selectedDrill, selectedIdx,
  onSelectDrill, onAdd, onDelete,
  onFieldChange, onMappingChange, onAddMapping, onDeleteMapping,
}: {
  body: DashboardBody
  project: string
  projectVisuals: ProjectVisualSummary[]
  projectDashboards: ProjectDashboardSummary[]
  selectedDrill: DashboardDrillThrough | null
  selectedIdx: number | null
  onSelectDrill: (idx: number | null) => void
  onAdd: () => void
  onDelete: (idx: number) => void
  onFieldChange: <K extends keyof DashboardDrillThrough>(
    idx: number, field: K, value: DashboardDrillThrough[K]
  ) => void
  onMappingChange: (
    idx: number, oldKey: string, newKey: string, newValue: string
  ) => void
  onAddMapping: (idx: number) => void
  onDeleteMapping: (idx: number, key: string) => void
}) {
  // Target-param names offered in the mapping dropdown, cached per target
  // (`D:<obj>` = a dashboard's GLOBAL_CRITERIA PARAM_NAMEs, `V:<obj>` = a
  // visual's prompt params). `null` = the fetch failed → free-text fallback.
  const [targetParamCache, setTargetParamCache] = useState<
    Record<string, string[] | null>
  >({})

  const targetType: DashboardDrillTargetType =
    selectedDrill?.TARGET_TYPE || 'dashboard'
  const targetKey = !selectedDrill
    ? ''
    : targetType === 'dashboard' && selectedDrill.TARGET_DASHBOARD_ID
      ? `D:${selectedDrill.TARGET_DASHBOARD_ID}`
      : targetType === 'visual' && selectedDrill.TARGET_VISUAL_ID
        ? `V:${selectedDrill.TARGET_VISUAL_ID}`
        : ''

  // Fetch the selected target's params once per target (same loaders the
  // preview / bindings UI use). A failed fetch is cached as null so the
  // mapping falls back to free text instead of refetching forever.
  useEffect(() => {
    if (!project || !targetKey || targetKey in targetParamCache) return
    let cancelled = false
    const kind = targetKey.slice(0, 1)
    const objName = targetKey.slice(2)
    const fetchParams = async (): Promise<string[]> => {
      if (kind === 'D') {
        const res = await loadDashboardObject(project, objName)
        if (!res?.content) throw new Error(`Dashboard ${objName} has no model`)
        const m = sanitizeDashboardModel(res.content, objName)
        return m.DASHBOARD.GLOBAL_CRITERIA
          .map((c) => c.PARAM_NAME)
          .filter(Boolean)
      }
      const res = await loadVisualObject(project, objName)
      if (!res?.content) throw new Error(`Visual ${objName} has no model`)
      return extractVisualParams(res.content).map((p) => p.name)
    }
    fetchParams()
      .then((names) => {
        if (cancelled) return
        setTargetParamCache((prev) => ({ ...prev, [targetKey]: names }))
      })
      .catch((err) => {
        console.error('Failed to load drill-through target params:', targetKey, err)
        if (cancelled) return
        setTargetParamCache((prev) => ({ ...prev, [targetKey]: null }))
        toast.error(
          `Could not load parameters of ${objName} — type the target parameter manually`
        )
      })
    return () => { cancelled = true }
  }, [project, targetKey, targetParamCache])

  // 'self' maps onto THIS dashboard's global criteria — no fetch needed.
  const selfParams = useMemo(
    () => body.GLOBAL_CRITERIA.map((c) => c.PARAM_NAME).filter(Boolean),
    [body.GLOBAL_CRITERIA]
  )
  const targetParamOptions: string[] | null =
    targetType === 'self'
      ? selfParams
      : targetKey
        ? targetParamCache[targetKey] ?? null
        : null

  return (
    <>
      <div
        style={{
          width: 300,
          flexShrink: 0,
          minHeight: 0,
          overflowY: 'auto',
          borderRight: '1px solid var(--color-border)',
          background: 'var(--color-bg-secondary)',
          padding: '14px 12px',
        }}
      >
        <SectionHeader
          right={
            <button
              className="btn btn-ghost btn-sm"
              onClick={onAdd}
              title="Add drill-through"
              style={{
                width: 24, height: 24, padding: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid var(--color-border)', borderRadius: 6,
              }}
            >
              <Plus size={13} />
            </button>
          }
        >
          Drill-through ({body.DRILL_THROUGH.length})
        </SectionHeader>
        {body.DRILL_THROUGH.length === 0 && (
          <div style={emptyHintStyle}>
            No drill-through links. Add one to navigate from a tile to another
            dashboard.
          </div>
        )}
        {body.DRILL_THROUGH.map((d, i) => (
          <div
            key={i}
            onClick={() => onSelectDrill(i)}
            style={{
              ...chipStyle,
              flexDirection: 'column',
              alignItems: 'stretch',
              border: selectedIdx === i
                ? '1px solid var(--color-accent)'
                : '1px solid var(--color-border)',
              background:
                selectedIdx === i ? accentWeak : 'var(--color-surface-2)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
              {/* Two-line mono "source →⏎ target" (spec §9.5). */}
              <span style={{
                flex: 1, minWidth: 0,
                fontSize: 13.5, fontWeight: 600, fontFamily: MONO_FONT,
                lineHeight: 1.4,
              }}>
                <div style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {d.SOURCE_VISUAL_ID || '(source)'} →
                </div>
                <div style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {drillTargetLabel(d)}
                </div>
              </span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(i)
                }}
                title="Remove"
                style={{ padding: '2px 4px' }}
              >
                <Trash2 size={12} />
              </button>
            </div>
            <div
              style={{
                fontSize: 14,
                color: 'var(--color-text-faint)',
              }}
            >
              {Object.keys(d.PARAM_MAPPING).length} param mapping(s)
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: '16px 20px',
          background: 'var(--color-bg)',
        }}
      >
        <SectionHeader>Drill-through Inspector</SectionHeader>
        {!selectedDrill || selectedIdx == null ? (
          <div style={emptyHintStyle}>
            Select or add a drill-through entry to edit it.
          </div>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                marginBottom: 16,
                maxWidth: 900,
              }}
            >
              <Field label="Source Visual">
                {/* Searchable popup picker — selecting from this dashboard's
                    own tiles. Stored value is the tile's VISUAL_ID. */}
                <ObjectPickerField
                  value={selectedDrill.SOURCE_VISUAL_ID}
                  onChange={(visualId) =>
                    onFieldChange(selectedIdx, 'SOURCE_VISUAL_ID', visualId)
                  }
                  catalog={body.VISUALS.map((v) => ({
                    obj_name: v.VISUAL_ID,
                    title: v.VISUAL_NAME,
                    hint: v.VISUAL_CONFIG_REF || undefined,
                  }))}
                  typeLabel="tile"
                  allowCustom={false}
                  placeholder="Pick a tile from this dashboard…"
                  emptyHint="No tiles on this dashboard yet — add one from the Layout tab."
                />
              </Field>
              <Field label="Target Type">
                <select
                  value={targetType}
                  onChange={(e) =>
                    onFieldChange(
                      selectedIdx,
                      'TARGET_TYPE',
                      e.target.value as DashboardDrillTargetType
                    )
                  }
                  style={selectStyle}
                >
                  {DRILL_TARGET_TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              {targetType === 'dashboard' && (
                <Field label="Target Dashboard">
                  {/* Picker over the project's dashboards. Custom names stay
                      allowed so a not-yet-created dashboard (or a failed list
                      fetch) can still be referenced by name. */}
                  <ObjectPickerField
                    value={selectedDrill.TARGET_DASHBOARD_ID}
                    onChange={(objName) =>
                      onFieldChange(selectedIdx, 'TARGET_DASHBOARD_ID', objName)
                    }
                    catalog={projectDashboards.map((d) => ({
                      obj_name: d.obj_name,
                      title: d.dashboard_name || d.obj_name,
                      hint: `${d.visual_count} tile${d.visual_count === 1 ? '' : 's'}`,
                    }))}
                    typeLabel="dashboard"
                    placeholder="Pick a dashboard…"
                    emptyHint="No dashboards found in this project."
                  />
                </Field>
              )}
              {targetType === 'visual' && (
                <Field label="Target Visual">
                  <ObjectPickerField
                    value={selectedDrill.TARGET_VISUAL_ID || ''}
                    onChange={(objName) =>
                      onFieldChange(selectedIdx, 'TARGET_VISUAL_ID', objName)
                    }
                    catalog={projectVisuals.map((v) => ({
                      obj_name: v.obj_name,
                      title: v.obj_name,
                      hint: v.visual_type || v.schema_name || undefined,
                    }))}
                    typeLabel="visual"
                    placeholder="Pick a visual…"
                    emptyHint="No visuals found in this project."
                  />
                </Field>
              )}
              {targetType === 'self' && (
                <div style={{ ...hintStyle, alignSelf: 'end', paddingBottom: 10 }}>
                  Clicking a row/point applies the mapped values to this
                  dashboard's own global criteria (cross-filter) — no
                  navigation, no target to pick.
                </div>
              )}
            </div>

            <SectionHeader
              right={
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => onAddMapping(selectedIdx)}
                  style={{
                    width: 24, height: 24, padding: 0,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    border: '1px solid var(--color-border)', borderRadius: 6,
                  }}
                >
                  <Plus size={13} />
                </button>
              }
            >
              Param Mapping (source column →{' '}
              {targetType === 'visual'
                ? 'target visual param'
                : targetType === 'self'
                  ? 'this dashboard\'s criterion'
                  : 'target dashboard param'})
            </SectionHeader>
            {Object.keys(selectedDrill.PARAM_MAPPING).length === 0 ? (
              <div style={emptyHintStyle}>
                {targetType === 'self'
                  ? "No mappings. Add one to set this dashboard's global criteria from the clicked row's column value."
                  : `No mappings. Add one to pre-fill the target ${targetType}'s filter from the clicked row's column value.`}
              </div>
            ) : (
              <div style={{
                display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 900,
              }}>
                {Object.entries(selectedDrill.PARAM_MAPPING).map(
                  ([key, value]) => (
                    <div
                      key={key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      <input
                        type="text"
                        value={key}
                        onChange={(e) =>
                          onMappingChange(
                            selectedIdx,
                            key,
                            e.target.value,
                            value
                          )
                        }
                        placeholder="DWH_SALES_SUM_CUST_CODE"
                        style={{
                          ...inputStyle, flex: 1,
                          fontFamily: MONO_FONT, fontSize: 15,
                        }}
                      />
                      <span
                        style={{
                          color: 'var(--color-text-secondary)',
                          fontSize: 13,
                        }}
                      >
                        →
                      </span>
                      {targetParamOptions && targetParamOptions.length > 0 ? (
                        // Known target → offer its real params. The current
                        // value stays selectable even if it isn't one of them
                        // (hand-authored / stale models).
                        <select
                          value={value}
                          onChange={(e) =>
                            onMappingChange(
                              selectedIdx,
                              key,
                              key,
                              e.target.value
                            )
                          }
                          style={{
                            ...selectStyle, flex: 1,
                            fontFamily: MONO_FONT, fontSize: 15,
                          }}
                        >
                          <option value="">— target param —</option>
                          {value !== '' && !targetParamOptions.includes(value) && (
                            <option value={value}>{value} (custom)</option>
                          )}
                          {targetParamOptions.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      ) : (
                        // No target picked yet / params unavailable → free text.
                        <input
                          type="text"
                          value={value}
                          onChange={(e) =>
                            onMappingChange(
                              selectedIdx,
                              key,
                              key,
                              e.target.value
                            )
                          }
                          placeholder="FILTER_CUST_CODE"
                          style={{
                            ...inputStyle, flex: 1,
                            fontFamily: MONO_FONT, fontSize: 15,
                          }}
                        />
                      )}
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => onDeleteMapping(selectedIdx, key)}
                        title="Remove mapping"
                        style={{ padding: '2px 4px' }}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )
                )}
              </div>
            )}
          </>
        )}
      </div>
    </>
  )
}

// ── Shared layout primitives (mirror VisualDesignerPage) ───────
// Section eyebrow — small, bold, uppercase, letterspaced (spec §0.3).
function SectionHeader({
  children, right,
}: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 13,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        color: 'var(--color-text-faint)',
        marginTop: 12,
        marginBottom: 8,
      }}
    >
      <span>{children}</span>
      {right}
    </div>
  )
}

function Field({
  label, children,
}: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      {children}
    </div>
  )
}

// Theme-safe "accent-weak" wash for selected cards/tiles — the prototype
// token from globals.css (spec §0.2), themed for dark and light.
const accentWeak = 'var(--color-accent-weak)'

/** SQL / identifier font per spec §0.3 — mirrors VisualDesignerPage. */
const MONO_FONT = "var(--font-mono, 'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace)"

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  fontSize: 15.5,
  border: '1px solid var(--color-border)',
  borderRadius: 7,
  background: 'var(--color-input-bg)',
  color: 'var(--color-text)',
}

const selectStyle: React.CSSProperties = { ...inputStyle }

const chipStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 10px',
  border: '1px solid var(--color-border)',
  borderRadius: 8,
  marginBottom: 6,
  cursor: 'pointer',
  userSelect: 'none',
}

const hintStyle: React.CSSProperties = {
  fontSize: 14,
  color: 'var(--color-text-secondary)',
  marginTop: 4,
  lineHeight: 1.45,
}

const emptyHintStyle: React.CSSProperties = {
  fontSize: 14.5,
  color: 'var(--color-text-secondary)',
  fontStyle: 'italic',
  padding: '4px 6px',
}
